import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import GrindrodLogo from "./GrindrodLogo";
import {
    LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine, CartesianGrid,
} from "recharts";
import {
    ShieldCheck, Gauge as GaugeIcon, Handshake, Users, Boat, Anchor, Package, Clock,
    ChartLineUp, ChatCircle, Star, CheckCircle, Calendar, Wrench, Lightning,
    UserPlus, Truck, Target, CurrencyDollar, Megaphone, Info, TrendUp, TrendDown,
    Train,
} from "@phosphor-icons/react";

/* ------------------------------------------------------------------ helpers */

const fmtTons = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtZAR = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;
const fmtHrs = (n) => `${(Number(n) || 0).toFixed(1)}`;

const ratingDot = (r) => ({
    good: "bg-[var(--grindrod-green)]",
    avg: "bg-[#FFB300]",
    poor: "bg-[var(--grindrod-red)]",
}[r] || "bg-slate-300");

const ratingPillClass = (r) => ({
    GOOD: "bg-[var(--grindrod-green-bg)] text-[#00A344] border-[var(--grindrod-green)]",
    WATCH: "bg-[#FFF8E1] text-[#B36B00] border-[#FFB300]",
    POOR: "bg-[#FDE7E9] text-[var(--grindrod-red)] border-[var(--grindrod-red)]",
}[r] || "bg-slate-100 text-slate-600 border-slate-300");

const monthInputValue = (d) => {
    const dt = d || new Date();
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
};

const periodLabelFromInput = (period, monthInput, twoWeeklyStart) => {
    if (period === "monthly") return monthInput; // YYYY-MM
    return `${twoWeeklyStart}_2W`;
};

const dateLabel = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/* ----------------------------------------------------------------- Component */

export default function TerminalReport() {
    const { user } = useAuth();
    const [period, setPeriod] = useState("monthly");
    const [monthInput, setMonthInput] = useState(monthInputValue(new Date()));
    const [twoWeeklyStart, setTwoWeeklyStart] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 13);
        return d.toISOString().slice(0, 10);
    });
    const [operation, setOperation] = useState("terminal");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [modal, setModal] = useState(null); // 'complaint' | 'safety' | 'overtime' | 'labour' | 'finance' | 'target'
    const [manageKind, setManageKind] = useState(null); // 'complaint' | 'safety' | 'overtime' | 'breakdown' | 'labour' | 'finance' | 'target'
    const [detailKind, setDetailKind] = useState(null); // read-only "More info" drill-down

    const startDate = period === "monthly" ? `${monthInput}-01` : twoWeeklyStart;
    const periodLabel = useMemo(() => periodLabelFromInput(period, monthInput, twoWeeklyStart), [period, monthInput, twoWeeklyStart]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const res = await api.get("/terminal-report", {
                    params: { period, start_date: startDate, operation },
                });
                if (!cancelled) setData(res.data);
            } catch {
                toast.error("Failed to load terminal report");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [period, startDate, operation, refreshKey]);

    const refresh = () => setRefreshKey((k) => k + 1);

    const role = user?.role;
    const canInputComplaint = ["admin", "manager", "client_liaison"].includes(role);
    const canInputSafety = ["admin", "manager", "safety"].includes(role);
    const canInputFinance = ["admin", "manager", "finance"].includes(role);
    const canInputOvertime = ["admin", "manager"].includes(role);
    const canInputBreakdown = ["admin", "manager"].includes(role);
    const canInputTarget = ["admin", "manager"].includes(role);

    const periodHeading = period === "monthly"
        ? new Date(`${monthInput}-01`).toLocaleDateString("en-GB", { month: "long", year: "numeric" }).toUpperCase()
        : data ? `${dateLabel(data.start)} – ${dateLabel(new Date(new Date(data.end).getTime() - 86400000).toISOString())}` : "";

    return (
        <div className="space-y-4" data-testid="terminal-report">
            {/* Period & operation selector */}
            <div className="g-card p-4 flex flex-wrap items-center gap-3" data-testid="terminal-report-controls">
                <div className="flex items-center gap-2">
                    <span className="uppercase-label">Period</span>
                    <select value={period} onChange={(e) => setPeriod(e.target.value)} className="g-input !py-1.5 !px-2 text-xs w-auto" data-testid="period-select">
                        <option value="monthly">Monthly</option>
                        <option value="two_weekly">Two-Weekly</option>
                    </select>
                    {period === "monthly" ? (
                        <input type="month" value={monthInput} onChange={(e) => setMonthInput(e.target.value)} className="g-input !py-1.5 !px-2 text-xs w-auto" data-testid="month-input" />
                    ) : (
                        <input type="date" value={twoWeeklyStart} onChange={(e) => setTwoWeeklyStart(e.target.value)} className="g-input !py-1.5 !px-2 text-xs w-auto" data-testid="two-weekly-start" />
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <span className="uppercase-label">Scope</span>
                    <select value={operation} onChange={(e) => setOperation(e.target.value)} className="g-input !py-1.5 !px-2 text-xs w-auto" data-testid="operation-select">
                        <option value="terminal">Whole Terminal</option>
                        <option value="rb_vessel">RB Vessel</option>
                        <option value="coal_tippler">Coal Tippler</option>
                        <option value="rock_tippler">Rock Tippler</option>
                        <option value="tronox">Tronox I/O</option>
                    </select>
                </div>
            </div>

            {/* Departmental capture panel — tabbed: each dept fills + manages their section */}
            <DepartmentCapturePanel role={role} onOpen={setModal} onManage={setManageKind} />

            {loading && !data && (
                <div className="g-card p-12 text-center text-slate-400" data-testid="terminal-report-loading">Loading board-room dashboard…</div>
            )}

            {data && (
                <div className="space-y-4" data-testid="terminal-report-body">
                    {/* HERO */}
                    <Hero data={data} periodHeading={periodHeading} period={period} />

                    {/* ROW 1 — Executive summary | Vessel performance | Performance trends */}
                    <div className="grid gap-4 lg:grid-cols-12" data-testid="terminal-report-row1">
                        <div className="lg:col-span-3"><ExecutiveSummary data={data} onMoreInfo={() => setDetailKind("executive_summary")} /></div>
                        <div className="lg:col-span-6"><VesselPerformanceTable data={data} onMoreInfo={() => setDetailKind("vessel_performance")} /></div>
                        <div className="lg:col-span-3"><PerformanceTrends data={data} onMoreInfo={() => setDetailKind("trends")} /></div>
                    </div>

                    {/* ROW 2 — Complaints | Breakdowns | Overtime | Labour */}
                    <div className="grid gap-4 lg:grid-cols-12" data-testid="terminal-report-row2">
                        <div className="lg:col-span-3"><ComplaintsCard data={data} onMoreInfo={() => setDetailKind("complaint")} /></div>
                        <div className="lg:col-span-3"><BreakdownsCard data={data} onMoreInfo={() => setDetailKind("breakdown")} /></div>
                        <div className="lg:col-span-3"><OvertimeCard data={data} onMoreInfo={() => setDetailKind("overtime")} /></div>
                        <div className="lg:col-span-3"><LabourCard data={data} onMoreInfo={() => setDetailKind("labour")} /></div>
                    </div>

                    {/* ROW 2.5 — Safety incidents (full list — critical info) */}
                    <SafetyIncidentsCard data={data} onMoreInfo={() => setDetailKind("safety")} />

                    {/* ROW 2.55 — Rail & Coal-Tippler consignments in period */}
                    <RailAndTipplerCard data={data} />

                    {/* ROW 2.6 — Vessel finance vs monthly budget */}
                    <VesselFinanceCard data={data} user={user} onRefresh={refresh} />

                    {/* ROW 3 — Finance | Key takeaways | Recommendations | Next actions */}
                    <div className="grid gap-4 lg:grid-cols-12" data-testid="terminal-report-row3">
                        <div className="lg:col-span-3"><FinanceCard data={data} onMoreInfo={() => setDetailKind("finance")} /></div>
                        <div className="lg:col-span-3"><BulletCard title="Key Takeaways" Icon={CheckCircle} accent="#00A344" items={data.key_takeaways} testid="key-takeaways" /></div>
                        <div className="lg:col-span-3"><BulletCard title="Recommendations" Icon={Star} accent="#FFB300" items={data.recommendations} testid="recommendations" /></div>
                        <div className="lg:col-span-3"><BulletCard title="Next Actions" Icon={Calendar} accent="var(--grindrod-navy)" items={data.next_actions} testid="next-actions" /></div>
                    </div>

                    <div className="text-xs text-slate-400 italic px-2">
                        Performance data is preliminary and subject to final reconciliation.
                    </div>
                </div>
            )}

            {/* Modals */}
            {modal === "complaint" && <ComplaintModal onClose={() => setModal(null)} onSaved={refresh} />}
            {modal === "safety" && <SafetyModal onClose={() => setModal(null)} onSaved={refresh} />}
            {modal === "breakdown" && <BreakdownModal onClose={() => setModal(null)} onSaved={refresh} />}
            {modal === "overtime" && <OvertimeModal onClose={() => setModal(null)} onSaved={refresh} />}
            {modal === "labour" && <LabourModal onClose={() => setModal(null)} onSaved={refresh} />}
            {modal === "finance" && <FinanceModal periodLabel={periodLabel} onClose={() => setModal(null)} onSaved={refresh} />}
            {modal === "target" && <TargetModal periodLabel={periodLabel} operation={operation} onClose={() => setModal(null)} onSaved={refresh} />}

            {manageKind && (
                <ManageEntriesModal kind={manageKind}
                                    periodLabel={periodLabel}
                                    startDate={data?.start}
                                    endDate={data?.end}
                                    onClose={() => setManageKind(null)}
                                    onChanged={refresh} />
            )}

            {detailKind && (
                <DetailDrillModal
                    kind={detailKind}
                    data={data}
                    periodLabel={periodLabel}
                    startDate={data?.start}
                    endDate={data?.end}
                    onClose={() => setDetailKind(null)}
                />
            )}
        </div>
    );
}

function ToolbarBtn({ onClick, icon: Icon, label, testid }) {
    return (
        <button onClick={onClick} className="btn-outline !py-1.5 !px-2.5 text-[11px] inline-flex items-center gap-1.5" data-testid={testid}>
            <Icon size={13} weight="bold" /> {label}
        </button>
    );
}

/* ===================== DEPARTMENT CAPTURE PANEL =====================
   Tabbed, mobile-friendly. Each department shows ADD buttons + MANAGE
   buttons (with delete) for their existing entries in the current period. */
const DEPARTMENT_INPUTS = [
    {
        key: "management", title: "Admin / Manager", short: "Admin", roles: ["admin", "manager"], accent: "var(--grindrod-navy)", Icon: Target,
        items: [
            { modal: "target", manageKind: "target", label: "Monthly targets", desc: "Tons / voyages / on-time / turnaround", Icon: Target, testid: "dept-set-targets-btn" },
        ],
    },
    {
        key: "planner_elm", title: "Planner & Electrical Line Mgr", short: "Planner / ELM", roles: ["admin", "manager"], accent: "#5A3A22", Icon: Wrench,
        items: [
            { modal: "breakdown", manageKind: "breakdown", label: "Breakdowns", desc: "Mechanical / electrical · major + minor + downtime", Icon: Wrench, testid: "dept-log-breakdown-btn" },
            { modal: "overtime", manageKind: "overtime", label: "Overtime hours", desc: "Operations / Mechanical / Electrical hours per week", Icon: Clock, testid: "dept-log-overtime-btn" },
        ],
    },
    {
        key: "safety", title: "Safety Department", short: "Safety", roles: ["admin", "safety"], accent: "#00A344", Icon: ShieldCheck,
        items: [
            { modal: "safety", manageKind: "safety", label: "Safety incidents", desc: "Near-miss · first-aid · medical · LTIFR · property damage", Icon: ShieldCheck, testid: "dept-log-safety-btn" },
        ],
    },
    {
        key: "client_liaison", title: "Client Liaison", short: "Client Liaison", roles: ["admin", "client_liaison"], accent: "#1E40AF", Icon: ChatCircle,
        items: [
            { modal: "complaint", manageKind: "complaint", label: "Client complaints", desc: "Category · client · vessel · description", Icon: ChatCircle, testid: "dept-log-complaint-btn" },
        ],
    },
    {
        key: "finance", title: "Finance Department", short: "Finance", roles: ["admin", "finance"], accent: "#B36B00", Icon: CurrencyDollar,
        items: [
            { modal: "labour", manageKind: "labour", label: "Labour / plant hire", desc: "General workers · fitters · electricians · plant equipment", Icon: UserPlus, testid: "dept-log-labour-btn" },
            { modal: "finance", manageKind: "finance", label: "Finance entries", desc: "Description · budget vs actual (ZAR)", Icon: CurrencyDollar, testid: "dept-log-finance-btn" },
        ],
    },
];

function DepartmentCapturePanel({ role, onOpen, onManage }) {
    const visible = DEPARTMENT_INPUTS.filter((d) => d.roles.includes(role));
    const [active, setActive] = useState(visible[0]?.key || null);
    useEffect(() => {
        if (!visible.find((d) => d.key === active)) setActive(visible[0]?.key || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role]);
    if (visible.length === 0) return null;
    const current = visible.find((d) => d.key === active) || visible[0];

    return (
        <div className="g-card overflow-hidden" data-testid="department-capture-panel">
            <div className="g-card-header-dark">
                <PencilLineIcon />
                <div className="font-display font-bold uppercase tracking-wide">Capture & manage data</div>
                <span className="ml-auto text-[10px] text-slate-300 uppercase tracking-wider hidden sm:inline">Add, edit or remove entries</span>
            </div>

            {/* Tab bar — horizontally scrollable on mobile */}
            <div className="border-b border-slate-200 bg-slate-50 overflow-x-auto" data-testid="dept-tab-bar">
                <div className="flex min-w-max">
                    {visible.map((dept) => {
                        const isActive = dept.key === active;
                        return (
                            <button
                                key={dept.key}
                                onClick={() => setActive(dept.key)}
                                data-testid={`dept-tab-${dept.key}`}
                                className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-[11px] sm:text-xs font-display font-bold uppercase tracking-wide whitespace-nowrap border-b-2 transition-colors ${
                                    isActive
                                        ? "border-[var(--grindrod-red)] text-[var(--grindrod-navy)] bg-white"
                                        : "border-transparent text-slate-500 hover:text-[var(--grindrod-navy)] hover:bg-white/60"
                                }`}
                            >
                                <span className="w-5 h-5 rounded-sm flex items-center justify-center text-white flex-shrink-0" style={{ background: dept.accent }}>
                                    <dept.Icon size={11} weight="fill" />
                                </span>
                                <span className="hidden sm:inline">{dept.title}</span>
                                <span className="sm:hidden">{dept.short}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Selected department actions */}
            <div className="p-3 sm:p-4 bg-white" data-testid={`dept-actions-${current.key}`}>
                <div className="grid gap-2.5 sm:grid-cols-2">
                    {current.items.map((it) => (
                        <div key={it.modal} className="rounded-sm border border-slate-200 p-2.5 flex flex-col">
                            <div className="flex items-center gap-1.5 mb-1">
                                <it.Icon size={13} weight="bold" style={{ color: current.accent }} />
                                <span className="font-display font-bold text-[12px] text-[var(--grindrod-navy)]">{it.label}</span>
                            </div>
                            <div className="text-[10.5px] text-slate-500 leading-snug mb-2.5 flex-1">{it.desc}</div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => onOpen(it.modal)}
                                    data-testid={it.testid}
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-[var(--grindrod-red)] hover:bg-red-700 text-white text-[11px] font-display font-bold uppercase tracking-wide transition-colors"
                                >
                                    <PlusIcon /> Add new
                                </button>
                                <button
                                    onClick={() => onManage(it.manageKind)}
                                    data-testid={`${it.testid}-manage`}
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-slate-300 hover:border-[var(--grindrod-navy)] hover:bg-slate-50 text-[var(--grindrod-navy)] text-[11px] font-display font-bold uppercase tracking-wide transition-colors"
                                >
                                    <ListIcon /> Manage
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function PlusIcon() {
    return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M12 5v14M5 12h14" strokeLinecap="round" /></svg>;
}
function ListIcon() {
    return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" strokeLinecap="round" /></svg>;
}
function TrashIcon() {
    return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function PencilLineIcon() {
    // small inline icon to avoid pulling another import
    return (
        <svg width="16" height="16" viewBox="0 0 256 256" className="text-white"><path fill="currentColor" d="M227.31 73.37 182.63 28.69a16 16 0 0 0-22.63 0L36.69 152A15.86 15.86 0 0 0 32 163.31V208a16 16 0 0 0 16 16h44.69a15.86 15.86 0 0 0 11.31-4.69L227.31 96a16 16 0 0 0 0-22.63ZM92.69 208H48v-44.69l88-88L180.69 120ZM192 108.69L147.31 64l24-24L216 84.69Z"/></svg>
    );
}


/* ===================== HERO ===================== */
function SeccpChip({ Icon, color, label, tag }) {
    return (
        <div
            className="ttv-seccp-chip"
            style={{ "--seccp-color": color }}
            data-testid={`seccp-chip-${label.toLowerCase()}`}
        >
            <div className="ttv-seccp-chip-icon">
                <Icon size={14} weight="fill" />
            </div>
            <div className="ttv-seccp-chip-body">
                <div className="ttv-seccp-chip-label">{label}</div>
                <div className="ttv-seccp-chip-tag">{tag}</div>
            </div>
        </div>
    );
}

function Hero({ data, periodHeading, period }) {
    return (
        <div className="g-card overflow-hidden" data-testid="terminal-report-hero">
            <div className="bg-gradient-to-r from-[var(--grindrod-navy)] via-[#0F1E3D] to-[var(--grindrod-navy)] text-white px-5 py-4 grid md:grid-cols-[auto_1fr_auto] items-center gap-4">
                <div className="flex items-center gap-3">
                    <div className="bg-white rounded-sm p-1.5"><GrindrodLogo size="sm" /></div>
                </div>
                <div className="text-center">
                    <div className="font-display font-black text-xl md:text-3xl tracking-wide leading-tight">TERMINAL REPORT DASHBOARD</div>

                    {/* SECCP framework — Grindrod's guiding principles */}
                    <div className="mt-2 inline-flex items-center gap-2 select-none">
                        <span className="h-px w-8 bg-gradient-to-r from-transparent to-[#5DA0FF]" />
                        <div className="font-display font-black text-lg md:text-2xl leading-none tracking-[0.18em] bg-clip-text text-transparent bg-gradient-to-b from-white to-[#B7C7DE]">
                            SECCP
                        </div>
                        <span className="h-px w-8 bg-gradient-to-l from-transparent to-[#5DA0FF]" />
                    </div>
                    <div className="text-[10px] md:text-[11px] text-slate-300 tracking-[0.24em] uppercase mt-1 font-semibold">
                        Safety · Execution · Customer · Cost · People
                    </div>

                    <div className="hidden md:flex flex-wrap justify-center gap-2 mt-3 text-[10px] uppercase tracking-wider text-slate-100 max-w-3xl mx-auto">
                        <SeccpChip Icon={ShieldCheck}  color="#22C55E"  label="Safety"    tag="We Care." />
                        <SeccpChip Icon={Wrench}       color="#5DA0FF"  label="Execution" tag="We Operate. We Maintain." />
                        <SeccpChip Icon={Handshake}    color="#F59E0B"  label="Customer"  tag="We Deliver." />
                        <SeccpChip Icon={TrendDown}    color="#14B8A6"  label="Cost"      tag="We Manage." />
                        <SeccpChip Icon={Users}        color="#A855F7"  label="People"    tag="We Grow." />
                    </div>

                    {/* Mobile — compact single-line strip */}
                    <div className="md:hidden mt-2 flex justify-center gap-3 flex-wrap text-[9px] uppercase tracking-wider text-slate-200 font-semibold">
                        <span className="inline-flex items-center gap-1"><ShieldCheck size={10} weight="fill" className="text-[#22C55E]" /> Safety</span>
                        <span className="inline-flex items-center gap-1"><Wrench     size={10} weight="fill" className="text-[#5DA0FF]" /> Execution</span>
                        <span className="inline-flex items-center gap-1"><Handshake  size={10} weight="fill" className="text-[#F59E0B]" /> Customer</span>
                        <span className="inline-flex items-center gap-1"><TrendDown  size={10} weight="fill" className="text-[#14B8A6]" /> Cost</span>
                        <span className="inline-flex items-center gap-1"><Users      size={10} weight="fill" className="text-[#A855F7]" /> People</span>
                    </div>
                </div>
                <div className="text-right border border-white/20 rounded-sm px-3 py-2 min-w-[160px]">
                    <div className="text-[10px] uppercase tracking-wider text-slate-300">Period ({period === "monthly" ? "Monthly" : "Two Weekly"})</div>
                    <div className="font-display font-black text-base md:text-lg leading-tight" data-testid="hero-period-heading">{periodHeading}</div>
                    <div className="text-[10px] mt-1 text-slate-300">
                        {period === "two_weekly" ? "Wk 1 + Wk 2 split" : "Halves: 1st / 2nd"}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ===================== EXECUTIVE SUMMARY ===================== */
function ExecutiveSummary({ data, onMoreInfo }) {
    const e = data.executive_summary;
    const rows = [
        { Icon: Boat, label: "Vessels Operated", val: e.vessels_operated, color: "var(--grindrod-navy)" },
        { Icon: Anchor, label: "Voyages Completed", val: `${e.voyages_completed} / ${e.voyages_planned}`, color: "var(--grindrod-navy)" },
        { Icon: Package, label: "Total Cargo (t)", val: fmtTons(e.total_cargo_tons), sub: e.target_cargo_tons ? `target ${fmtTons(e.target_cargo_tons)}` : "no target", color: "var(--grindrod-navy)" },
        { Icon: ChartLineUp, label: "On-Time Performance", val: fmtPct(e.on_time_pct), good: e.on_time_pct >= e.target_on_time_pct, color: "#00A344" },
        { Icon: Clock, label: "Avg Turnaround (hrs)", val: fmtHrs(e.avg_turnaround_hrs), good: e.avg_turnaround_hrs <= e.target_turnaround_hrs, color: "var(--grindrod-navy)" },
        { Icon: ShieldCheck, label: "Safety Incidents (LTIFR)", val: e.ltifr_count, sub: `${e.safety_incidents} total`, good: e.ltifr_count === 0, color: "var(--grindrod-green)" },
        { Icon: ChatCircle, label: "Client Complaints", val: e.client_complaints, good: e.client_complaints === 0, color: "var(--grindrod-navy)" },
    ];
    return (
        <Card title="Executive Summary" Icon={Star} testid="executive-summary" onMoreInfo={onMoreInfo}>
            <div className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                    <div key={r.label || i} className="flex items-center justify-between py-2.5 text-sm">
                        <div className="flex items-center gap-2.5 text-slate-700">
                            <r.Icon size={16} weight="duotone" style={{ color: r.color }} />
                            <span className="uppercase tracking-wide text-[10.5px] font-semibold text-slate-600">{r.label}</span>
                        </div>
                        <div className="text-right">
                            <div className={`font-display font-black tabular ${r.good === false ? "text-[var(--grindrod-red)]" : r.good ? "text-[var(--grindrod-green)]" : "text-[var(--grindrod-navy)]"}`}>{r.val}</div>
                            {r.sub && <div className="text-[9.5px] text-slate-400">{r.sub}</div>}
                        </div>
                    </div>
                ))}
            </div>
            <div className="border-t border-slate-100 pt-3 mt-2 flex items-center justify-between">
                <div className="uppercase-label text-slate-500">Overall Performance</div>
                <span className={`g-pill border ${ratingPillClass(e.overall_rating)} font-display font-black`} data-testid="overall-rating-pill">{e.overall_rating}</span>
            </div>
        </Card>
    );
}

/* ===================== VESSEL PERFORMANCE TABLE ===================== */
function VesselPerformanceTable({ data, onMoreInfo }) {
    const rows = data.vessel_performance || [];
    const t = data.vessel_performance_total || {};
    return (
        <Card title="Vessel Performance Overview" Icon={Boat} testid="vessel-performance-table" onMoreInfo={onMoreInfo}>
            <div className="overflow-x-auto -mx-2 -mb-2">
                <table className="w-full text-xs tabular">
                    <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide text-[9.5px]">
                        <tr>
                            <th className="text-left py-2 px-2.5">Vessel</th>
                            <th className="text-center px-1">Voy. P</th>
                            <th className="text-center px-1">Voy. C</th>
                            <th className="text-right px-1.5">Cargo (t)</th>
                            <th className="text-right px-1.5">On-Time</th>
                            <th className="text-right px-1.5">Turn. (h)</th>
                            <th className="text-right px-1.5">Util.</th>
                            <th className="text-center px-2">Rate</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={8} className="text-center text-slate-400 py-8" data-testid="vessel-performance-empty">No vessels in this period.</td></tr>
                        )}
                        {rows.map((r, i) => (
                            <tr key={r.vessel_name || i} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-2.5 font-semibold text-[var(--grindrod-navy)]">{r.vessel_name}</td>
                                <td className="text-center">{r.voyages_planned}</td>
                                <td className="text-center">{r.voyages_completed}</td>
                                <td className="text-right">{fmtTons(r.cargo_tons)}</td>
                                <td className={`text-right font-semibold ${r.on_time_pct === 100 ? "text-[var(--grindrod-green)]" : "text-[var(--grindrod-red)]"}`}>{fmtPct(r.on_time_pct)}</td>
                                <td className="text-right">{fmtHrs(r.avg_turnaround_hrs)}</td>
                                <td className="text-right">{fmtPct(r.utilisation_pct)}</td>
                                <td className="text-center"><span className={`inline-block w-2.5 h-2.5 rounded-full ${ratingDot(r.rating)}`}></span></td>
                            </tr>
                        ))}
                        {rows.length > 0 && (
                            <tr className="border-t-2 border-[var(--grindrod-navy)] bg-slate-50 font-display font-black text-[var(--grindrod-navy)]" data-testid="vessel-performance-total">
                                <td className="py-2 px-2.5 uppercase text-[10px]">Total / Avg</td>
                                <td className="text-center">{t.voyages_planned}</td>
                                <td className="text-center">{t.voyages_completed}</td>
                                <td className="text-right">{fmtTons(t.cargo_tons)}</td>
                                <td className="text-right text-[var(--grindrod-green)]">{fmtPct(t.on_time_pct)}</td>
                                <td className="text-right">{fmtHrs(t.avg_turnaround_hrs)}</td>
                                <td className="text-right text-[var(--grindrod-green)]">{fmtPct(t.utilisation_pct)}</td>
                                <td></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

/* ===================== PERFORMANCE TRENDS (rewritten · plain-English) ===================== */
function PerformanceTrends({ data, onMoreInfo }) {
    const trends = data.trends || {};
    const w1 = trends.week_1 || {};
    const w2 = trends.week_2 || {};
    const cmp = data.period_comparison || { previous: {}, deltas: {} };
    const half1 = data.period === "monthly" ? "1st half" : "Week 1";
    const half2 = data.period === "monthly" ? "2nd half" : "Week 2";

    const METRICS = useMemo(() => [
        {
            k: "on_time_pct",
            title: "On-Time Performance",
            unit: "%",
            colour: "#00A344",
            target: data.executive_summary?.target_on_time_pct || 95,
            domain: [0, 100],
            // Higher is better
            betterWhen: "higher",
            explain: (v, t) => v >= t
                ? `Above target — every vessel is leaving on time.`
                : v >= 75
                    ? `Below the ${t}% target — a handful of vessels left late.`
                    : `Well below the ${t}% target — review the delay reasons table.`,
        },
        {
            k: "avg_turnaround_hrs",
            title: "Avg Turnaround Time",
            unit: " h",
            colour: "#1E40AF",
            target: data.executive_summary?.target_turnaround_hrs || 30,
            domain: [0, "auto"],
            betterWhen: "lower",
            explain: (v, t) => v <= t
                ? `Faster than the ${t} h target — efficient loadings.`
                : `Slower than the ${t} h target — focus on reducing pause time.`,
        },
        {
            k: "utilisation_pct",
            title: "Berth Utilisation",
            unit: "%",
            colour: "#1E40AF",
            target: 75,
            domain: [0, 100],
            betterWhen: "higher",
            explain: (v) => v >= 75
                ? `Berths are well utilised — minimal idle time between vessels.`
                : `Lower utilisation — gaps between vessels could be tightened.`,
        },
    ], [data.executive_summary]);

    const [active, setActive] = useState("on_time_pct");
    const m = METRICS.find((x) => x.k === active);
    const curr = data.executive_summary?.[active] ?? 0;
    const prev = cmp.previous?.[active] ?? 0;
    const delta = cmp.deltas?.[active] || {};
    const series = [
        { name: `Previous (${cmp.previous?.period_label || ""})`, v: prev },
        { name: half1, v: w1[active] || 0 },
        { name: half2, v: w2[active] || 0 },
    ];

    const isUp = (m.betterWhen === "higher" && (delta.diff || 0) >= 0)
              || (m.betterWhen === "lower" && (delta.diff || 0) <= 0);
    const trendColour = (delta.diff === 0 || delta.diff === undefined) ? "#64748B"
        : isUp ? "#00A344" : "#E3000F";
    const TrendIcon = (delta.diff || 0) >= 0 ? TrendUp : TrendDown;
    const captionTxt = (() => {
        if (!cmp.previous?.period_label) return m.explain(curr, m.target);
        const word = m.k === "avg_turnaround_hrs"
            ? (delta.diff > 0 ? "slower" : delta.diff < 0 ? "faster" : "unchanged")
            : (delta.diff > 0 ? "up" : delta.diff < 0 ? "down" : "unchanged");
        const absDiff = Math.abs(delta.diff || 0).toFixed(1);
        return delta.diff === 0
            ? `Same as ${cmp.previous.period_label}. ${m.explain(curr, m.target)}`
            : `${word} ${absDiff}${m.unit.trim()} vs ${cmp.previous.period_label} (${prev}${m.unit}). ${m.explain(curr, m.target)}`;
    })();

    return (
        <Card title="Performance Trends" Icon={ChartLineUp} testid="performance-trends" onMoreInfo={onMoreInfo}>
            {/* Tab strip — pick which metric to view */}
            <div className="flex gap-1 mb-3 -mx-0.5 overflow-x-auto" data-testid="trend-tabs">
                {METRICS.map((x) => (
                    <button
                        key={x.k}
                        onClick={() => setActive(x.k)}
                        data-testid={`trend-tab-${x.k}`}
                        className={`flex-1 min-w-[110px] px-2 py-1.5 rounded-sm text-[10.5px] font-display font-bold uppercase tracking-wide border ${
                            active === x.k
                                ? "bg-[var(--grindrod-navy)] text-white border-[var(--grindrod-navy)]"
                                : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                        }`}
                    >
                        {x.title}
                    </button>
                ))}
            </div>

            {/* Headline — current value + trend arrow */}
            <div className="flex items-end justify-between mb-1" data-testid={`trend-headline-${active}`}>
                <div>
                    <div className="uppercase-label text-slate-500">{m.title} now</div>
                    <div className="font-display font-black text-3xl tabular leading-none" style={{ color: m.colour }}>
                        {Number(curr || 0).toFixed(m.k === "avg_turnaround_hrs" ? 1 : 1)}{m.unit}
                    </div>
                </div>
                <div className="text-right">
                    <div className="uppercase-label text-slate-500">vs {cmp.previous?.period_label || "prev."}</div>
                    <div className="font-display font-black text-lg tabular flex items-center gap-1 justify-end" style={{ color: trendColour }} data-testid={`trend-delta-${active}`}>
                        <TrendIcon size={16} weight="bold" />
                        {delta.diff !== undefined ? `${delta.diff > 0 ? "+" : ""}${delta.diff}${m.unit.trim()}` : "—"}
                    </div>
                    <div className="text-[10.5px] text-slate-500">target {m.target}{m.unit.trim()}</div>
                </div>
            </div>

            {/* Plain-English caption */}
            <div className="rounded-sm bg-slate-50 border border-slate-200 px-2.5 py-2 text-[12px] text-slate-700 mb-3" data-testid={`trend-caption-${active}`}>
                {captionTxt}
            </div>

            {/* Single big chart with target line */}
            <div style={{ height: 180 }} data-testid={`trend-chart-${active}`}>
                <ResponsiveContainer>
                    <LineChart data={series} margin={{ top: 16, right: 20, left: 0, bottom: 4 }}>
                        <CartesianGrid stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} stroke="#CBD5E1" />
                        <YAxis tick={{ fontSize: 10, fill: "#64748B" }} stroke="#CBD5E1" domain={m.domain} width={38}
                               tickFormatter={(v) => `${Math.round(v)}${m.unit.trim()}`} />
                        <Tooltip
                            formatter={(v) => `${Number(v).toFixed(1)}${m.unit}`}
                            contentStyle={{ fontSize: 12 }}
                        />
                        {m.target > 0 && (
                            <ReferenceLine y={m.target} stroke="#E3000F" strokeDasharray="4 3" strokeWidth={1.5}
                                           label={{ value: `Target ${m.target}${m.unit.trim()}`, position: "right", fill: "#E3000F", fontSize: 10, fontWeight: "bold" }} />
                        )}
                        <Line type="monotone" dataKey="v" stroke={m.colour} strokeWidth={3}
                              dot={{ r: 5, fill: m.colour, stroke: "white", strokeWidth: 2 }}
                              label={{ position: "top", fill: m.colour, fontSize: 11, fontWeight: "bold", formatter: (v) => `${Number(v).toFixed(1)}${m.unit.trim()}` }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Reading guide */}
            <div className="mt-3 text-[11px] text-slate-500 border-t border-slate-100 pt-2">
                <span className="inline-flex items-center gap-1 mr-3"><span className="inline-block w-3 h-0.5" style={{ background: m.colour }}></span> Actual</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0 border-t-2 border-dashed" style={{ borderColor: "#E3000F" }}></span> Target</span>
                <span className="ml-3 text-slate-400">{m.betterWhen === "higher" ? "Higher is better." : "Lower is better."}</span>
            </div>
        </Card>
    );
}

/* ===================== COMPLAINTS ===================== */
function ComplaintsCard({ data, onMoreInfo }) {
    const cats = data.complaints_by_category || {};
    const labels = {
        delays_turnaround: "Delays / Turnaround Time",
        communication: "Communication",
        documentation: "Documentation",
        cargo_condition: "Cargo Condition / Damage",
        other: "Other",
    };
    const totalAll = Object.values(cats).reduce((a, [w1, w2]) => a + w1 + w2, 0);
    return (
        <Card title="Client Complaints" Icon={ChatCircle} testid="complaints-card" onMoreInfo={onMoreInfo}>
            <table className="w-full text-xs tabular">
                <thead className="text-slate-500 uppercase text-[9.5px] tracking-wide">
                    <tr>
                        <th className="text-left py-1">Category</th>
                        <th className="text-center px-1">Wk 1</th>
                        <th className="text-center px-1">Wk 2</th>
                        <th className="text-center px-1">Total</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(cats).map(([k, [w1, w2]]) => (
                        <tr key={k} className="border-t border-slate-100">
                            <td className="py-1.5 text-slate-700">{labels[k] || k}</td>
                            <td className="text-center">{w1}</td>
                            <td className="text-center">{w2}</td>
                            <td className="text-center font-semibold">{w1 + w2}</td>
                        </tr>
                    ))}
                    <tr className="border-t-2 border-[var(--grindrod-navy)] font-display font-bold text-[var(--grindrod-navy)]">
                        <td className="py-1.5 uppercase text-[10px]">Total</td>
                        <td className="text-center">{Object.values(cats).reduce((a, [w1]) => a + w1, 0)}</td>
                        <td className="text-center">{Object.values(cats).reduce((a, [, w2]) => a + w2, 0)}</td>
                        <td className="text-center" data-testid="complaints-total">{totalAll}</td>
                    </tr>
                </tbody>
            </table>
            {data.top_complaint_category && totalAll > 0 && (
                <div className="mt-3 g-card p-2 bg-slate-50 text-xs flex items-start gap-2">
                    <Megaphone size={14} weight="fill" className="text-[var(--grindrod-red)] flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-display font-bold text-[var(--grindrod-navy)] uppercase text-[10px]">Top Complaint</div>
                        <div className="text-slate-700">{labels[data.top_complaint_category]}</div>
                    </div>
                </div>
            )}
        </Card>
    );
}

/* ===================== RAIL & COAL-TIPPLER CONSIGNMENTS ===================== */
function RailAndTipplerCard({ data }) {
    const rail = data.rail || { consignments_total: 0, items: [] };
    const tip = data.tippler || { consignments_total: 0, items: [] };
    const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-GB", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    }) : "—";
    const dmin = (m) => {
        if (m == null || !isFinite(m)) return "—";
        if (Math.abs(m) < 60) return `${Math.round(m)} m`;
        const h = Math.floor(Math.abs(m) / 60);
        const mm = Math.round(Math.abs(m) % 60);
        return `${m < 0 ? "-" : ""}${h}h ${mm}m`;
    };
    const legLabel = { delay_dispatch_min: "Dispatch", delay_transnet_arr_min: "→ Transnet",
                       delay_transnet_release_min: "Release", delay_grindrod_arr_min: "→ Grindrod" };
    return (
        <div className="g-card overflow-hidden" data-testid="rail-tippler-card">
            <div className="g-card-header-dark">
                <Train size={14} weight="fill" className="text-amber-300" />
                <div className="font-display font-black uppercase tracking-wide">
                    Rail &amp; Coal-Tippler Consignments · {rail.consignments_total} rail · {tip.consignments_total} tippler
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200">
                {/* RAIL side */}
                <div className="bg-white p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-widest font-black text-[var(--grindrod-navy)]">
                            Rail Consignments
                        </div>
                        <div className="text-[10px] text-slate-500">
                            {rail.consignments_completed} completed · {rail.wagons_total} wagons ·
                            {" "}Σ delay {dmin(rail.total_delay_min)} · avg {dmin(rail.avg_delay_min)}
                        </div>
                    </div>
                    {(!rail.items || !rail.items.length) ? (
                        <div className="text-sm text-slate-400 italic text-center py-4">
                            No rail consignments in this period.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-[11px] tabular" data-testid="rail-consignments-table">
                                <thead className="text-slate-500 uppercase text-[9.5px] tracking-wide">
                                    <tr>
                                        <th className="text-left py-1.5">Consignment</th>
                                        <th className="text-left py-1.5">Train · Mine</th>
                                        <th className="text-right py-1.5">Wagons</th>
                                        <th className="text-right py-1.5">Tons</th>
                                        {Object.keys(legLabel).map((k) => (
                                            <th key={k} className="text-right py-1.5">{legLabel[k]}</th>
                                        ))}
                                        <th className="text-right py-1.5 bg-slate-100">Σ delay</th>
                                        <th className="text-left py-1.5">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rail.items.map((r) => (
                                        <tr key={r.consignment_no} className="border-t border-slate-100 hover:bg-slate-50"
                                            data-testid={`rail-row-${r.consignment_no}`}>
                                            <td className="py-1 font-mono">{r.consignment_no}</td>
                                            <td className="py-1 text-slate-600">
                                                {r.train_id || "—"} · <span className="text-slate-500">{r.mine_name || "—"}</span>
                                            </td>
                                            <td className="py-1 text-right">{(r.wagons_tipped || 0)}/{r.wagons || 0}</td>
                                            <td className="py-1 text-right">{Number(r.tonnage || 0).toLocaleString()}</td>
                                            {Object.keys(legLabel).map((k) => (
                                                <td key={k} className={`py-1 text-right font-mono
                                                    ${(r.delays?.[k] || 0) > 15 ? "text-red-600 font-bold" : "text-slate-600"}`}>
                                                    {r.delays?.[k] != null ? dmin(r.delays[k]) : "—"}
                                                </td>
                                            ))}
                                            <td className="py-1 text-right font-bold bg-slate-50">{dmin(r.total_delay_min)}</td>
                                            <td className="py-1 text-slate-600 uppercase text-[10px]">{(r.status || "").replaceAll("_"," ")}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* TIPPLER side */}
                <div className="bg-white p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-widest font-black text-[var(--grindrod-navy)]">
                            Coal-Tippler Consignments
                        </div>
                        <div className="text-[10px] text-slate-500">
                            {tip.consignments_completed} completed ·
                            {" "}Σ delay {dmin(tip.total_delay_min)} · avg {dmin(tip.avg_delay_min)}
                        </div>
                    </div>
                    {(!tip.items || !tip.items.length) ? (
                        <div className="text-sm text-slate-400 italic text-center py-4">
                            No coal-tippler consignments in this period.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-[11px] tabular" data-testid="tippler-consignments-table">
                                <thead className="text-slate-500 uppercase text-[9.5px] tracking-wide">
                                    <tr>
                                        <th className="text-left py-1.5">Date · Shift</th>
                                        <th className="text-left py-1.5">Train</th>
                                        <th className="text-right py-1.5">Wagons</th>
                                        <th className="text-right py-1.5">Delays</th>
                                        <th className="text-right py-1.5">Breakdowns</th>
                                        <th className="text-right py-1.5 bg-slate-100">Delay time</th>
                                        <th className="text-left py-1.5">Rail link</th>
                                        <th className="text-left py-1.5">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tip.items.map((t) => (
                                        <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50"
                                            data-testid={`tippler-row-${t.id}`}>
                                            <td className="py-1 text-slate-600">
                                                {t.date} · <span className="uppercase text-slate-500">{t.shift || "—"}</span>
                                            </td>
                                            <td className="py-1 font-mono">{t.train_id || "—"}</td>
                                            <td className="py-1 text-right">{(t.current_wagon || 0)}/{t.wagon_count || 0}</td>
                                            <td className="py-1 text-right">{t.delays_count || 0}</td>
                                            <td className={`py-1 text-right ${(t.breakdown_count || 0) > 0 ? "text-red-600 font-bold" : "text-slate-600"}`}>
                                                {t.breakdown_count || 0}
                                            </td>
                                            <td className="py-1 text-right font-bold bg-slate-50">{dmin(t.delay_min)}</td>
                                            <td className="py-1 text-[10px] font-mono text-slate-500">
                                                {t.rail_consignment_id ? "linked" : "standalone"}
                                            </td>
                                            <td className="py-1 text-slate-600 uppercase text-[10px]">{t.status}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ===================== SAFETY INCIDENTS ===================== */
function SafetyIncidentsCard({ data, onMoreInfo }) {
    const sec = data.safety || { items: [], total: 0, ltifr_count: 0, total_hours_lost: 0, by_type: {} };
    const items = sec.items || [];
    const totalLost = sec.total_hours_lost || 0;
    const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
    const typePill = (t) => {
        const cls = t === "lost_time" ? "g-pill-red" : t === "medical" ? "g-pill-red" : t === "first_aid" ? "g-pill-amber" : t === "property_damage" ? "g-pill-amber" : "g-pill-navy";
        return <span className={`g-pill ${cls} normal-case tracking-normal capitalize`}>{(t || "").replace(/_/g, " ")}</span>;
    };
    return (
        <div className="g-card overflow-hidden" data-testid="safety-incidents-card">
            <div className="g-card-header-dark">
                <ShieldCheck size={14} weight="fill" className="text-[var(--grindrod-green)]" />
                <div className="font-display font-black uppercase tracking-wide">Safety incidents · {sec.total} this period · {totalLost.toFixed(1)} h lost to injury</div>
                {sec.ltifr_count > 0 && <span className="ml-2 g-pill g-pill-red normal-case tracking-normal" data-testid="safety-ltifr-pill">{sec.ltifr_count} LTIFR</span>}
                {onMoreInfo && (
                    <button onClick={onMoreInfo}
                            data-testid="safety-incidents-card-more-info"
                            className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-white/15 hover:bg-white/30 transition font-semibold">
                        <Info size={11} weight="fill" /> More info
                    </button>
                )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-slate-200 text-center">
                {Object.entries(sec.by_type || {}).map(([t, n]) => (
                    <div key={t} className="bg-white py-2">
                        <div className="text-[9.5px] uppercase tracking-wider text-slate-500 capitalize">{t.replace(/_/g, " ")}</div>
                        <div className="font-display font-black text-base tabular" style={{ color: t === "lost_time" || t === "medical" ? "var(--grindrod-red)" : "var(--grindrod-navy)" }}>{n}</div>
                    </div>
                ))}
            </div>
            <div className="p-3 bg-white">
                {items.length === 0 ? (
                    <div className="text-sm text-[var(--grindrod-green)] py-4 text-center" data-testid="safety-empty">
                        No safety incidents in this period — zero-harm operation.
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <table className="w-full text-xs tabular hidden sm:table" data-testid="safety-incidents-table">
                            <thead className="text-slate-500 uppercase text-[9.5px] tracking-wide">
                                <tr>
                                    <th className="text-left py-1.5">When</th>
                                    <th className="text-left py-1.5">Type</th>
                                    <th className="text-left py-1.5">Description</th>
                                    <th className="text-left py-1.5">Vessel</th>
                                    <th className="text-right py-1.5">Hrs lost</th>
                                    <th className="text-left py-1.5">Logged by</th>
                                </tr>
                            </thead>
                            <tbody>{items.map((s) => (
                                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`safety-row-${s.id}`}>
                                    <td className="py-1.5">{fmt(s.occurred_at)}</td>
                                    <td className="py-1.5">{typePill(s.incident_type)}</td>
                                    <td className="py-1.5 max-w-md truncate text-slate-700" title={s.description}>{s.description || "—"}</td>
                                    <td className="py-1.5 text-slate-600">{s.vessel_name || "—"}</td>
                                    <td className="py-1.5 text-right font-semibold">{Number(s.hours_lost || 0).toFixed(1)}</td>
                                    <td className="py-1.5 text-slate-500">{s.logged_by || "—"}</td>
                                </tr>))}
                            </tbody>
                        </table>
                        {/* Mobile cards */}
                        <div className="sm:hidden divide-y divide-slate-100 -mx-3" data-testid="safety-incidents-cards">
                            {items.map((s) => (
                                <div key={s.id} className="p-3" data-testid={`safety-card-${s.id}`}>
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        {typePill(s.incident_type)}
                                        <span className="text-[10px] text-slate-500 ml-auto">{fmt(s.occurred_at)}</span>
                                    </div>
                                    <div className="text-sm text-slate-700 mb-1">{s.description}</div>
                                    <div className="text-[10.5px] text-slate-500 flex flex-wrap gap-2">
                                        {s.vessel_name && <span>vessel · <b>{s.vessel_name}</b></span>}
                                        <span>· {Number(s.hours_lost || 0).toFixed(1)} h lost</span>
                                        {s.logged_by && <span>· by {s.logged_by}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/* ===================== VESSEL FINANCE vs MONTHLY BUDGET ===================== */
function VesselFinanceCard({ data, user, onRefresh }) {
    const vf = data.vessel_finance || { items: [], totals: {}, top_overspenders: [] };
    const t = vf.totals || {};
    const items = vf.items || [];
    const overspenders = vf.top_overspenders || [];
    const overMonthly = !!t.over_monthly_budget;
    const monthlyPct = t.monthly_budget_zar
        ? Math.min(100, Math.round((Math.max(t.monthly_actual_zar || 0, t.vessel_actual_zar || 0) / t.monthly_budget_zar) * 100))
        : 0;
    const canEditDivision = !!user && ["admin", "manager", "finance"].includes(user.role);
    const [editingVessel, setEditingVessel] = useState(null);
    const unassignedCount = items.filter((v) => v.division === "UNASSIGNED" || !["GTRB", "RBTG"].includes(v.division)).length;

    return (
        <div className="g-card overflow-hidden" data-testid="vessel-finance-card">
            <div className="g-card-header-dark">
                <CurrencyDollar size={14} weight="fill" className="text-amber-300" />
                <div className="font-display font-black uppercase tracking-wide">Vessel Finance · {t.vessels_in_period || 0} this period</div>
                {(t.vessels_over_budget || 0) > 0 && (
                    <span className="ml-2 g-pill g-pill-red normal-case tracking-normal" data-testid="vessel-finance-overbudget-pill">
                        {t.vessels_over_budget} over budget · {fmtZAR(t.total_overspend_zar || 0)}
                    </span>
                )}
                {overMonthly && (
                    <span className="ml-2 g-pill g-pill-red normal-case tracking-normal" data-testid="vessel-finance-monthly-pill">
                        Monthly budget exceeded
                    </span>
                )}
                {unassignedCount > 0 && (
                    <span className="ml-2 g-pill g-pill-amber normal-case tracking-normal" data-testid="vessel-finance-unassigned-pill">
                        {unassignedCount} unassigned · needs division
                    </span>
                )}
            </div>

            {/* Top KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-200">
                <KpiCell label="Monthly budget" value={fmtZAR(t.monthly_budget_zar || 0)} color="#1E40AF" testid="vf-kpi-monthly-budget" />
                <KpiCell label="Monthly actual" value={fmtZAR(Math.max(t.monthly_actual_zar || 0, t.vessel_actual_zar || 0))} color={overMonthly ? "var(--grindrod-red)" : "#B36B00"} testid="vf-kpi-monthly-actual" />
                <KpiCell label="Vessel costs (period)" value={fmtZAR(t.vessel_actual_zar || 0)} sub={`${fmtZAR(t.vessel_budget_zar || 0)} budgeted`} color="var(--grindrod-navy)" testid="vf-kpi-vessel-actual" />
                <KpiCell label="Variance" value={fmtZAR(t.monthly_variance_zar || 0)} sub={overMonthly ? "over budget" : "under budget"} color={overMonthly ? "var(--grindrod-red)" : "var(--grindrod-green)"} testid="vf-kpi-variance" />
            </div>

            {/* Per-division split — GTRB vs RBTG monthly budgets vs actual (UNASSIGNED appears if legacy vessels lack division) */}
            {(vf.by_division || []).some((d) => d.monthly_budget_zar || d.vessels_in_period) && (
                <div className={`grid grid-cols-1 ${(vf.by_division || []).length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-px bg-slate-200`} data-testid="vf-by-division">
                    {(vf.by_division || []).map((d) => (
                        <DivisionPanel key={d.division} d={d} />
                    ))}
                </div>
            )}

            {/* Burn-down bar */}
            <div className="px-3 py-2.5 bg-white border-b border-slate-200">
                <div className="flex items-center gap-2 mb-1">
                    <span className="uppercase-label">Monthly budget used</span>
                    <span className="ml-auto tabular text-xs font-display font-bold" style={{ color: overMonthly ? "var(--grindrod-red)" : "var(--grindrod-navy)" }}>{monthlyPct}%</span>
                </div>
                <div className="h-2 rounded-sm bg-slate-200 overflow-hidden">
                    <div className={overMonthly ? "h-full bg-[var(--grindrod-red)]" : "h-full bg-[var(--grindrod-green)]"} style={{ width: `${Math.min(100, monthlyPct)}%` }} />
                </div>
            </div>

            {/* Top overspenders strip (only when relevant) */}
            {overspenders.length > 0 && (
                <div className="px-3 py-2 bg-red-50 border-b border-red-200" data-testid="vf-overspenders-strip">
                    <div className="uppercase-label text-[var(--grindrod-red)] mb-1.5">Top contributors to the overrun</div>
                    <div className="flex flex-wrap gap-1.5">
                        {overspenders.map((v) => (
                            <span key={v.vessel_id} className="bg-white border border-red-300 text-[11px] rounded-sm px-2 py-0.5" data-testid={`vf-overspender-${v.vessel_id}`}>
                                <b className="text-[var(--grindrod-navy)]">{v.vessel_name}</b>
                                <span className="text-slate-500"> · {v.client_name}</span>
                                <span className="ml-1 text-[var(--grindrod-red)] tabular font-semibold">+{fmtZAR(v.overspend_zar)}</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Per-vessel table (desktop) + cards (mobile) */}
            <div className="bg-white">
                {items.length === 0 ? (
                    <div className="text-sm text-slate-400 py-8 text-center" data-testid="vf-empty">No vessels with finance data in this period.</div>
                ) : (
                    <>
                        <table className="w-full text-xs tabular hidden sm:table" data-testid="vf-table">
                            <thead className="text-slate-500 uppercase text-[9.5px] tracking-wide bg-slate-50">
                                <tr>
                                    <th className="text-left p-1.5 pl-3">Vessel</th>
                                    <th className="text-left p-1.5">Division</th>
                                    <th className="text-left p-1.5">Client</th>
                                    <th className="text-right p-1.5">Tons</th>
                                    <th className="text-right p-1.5">Budget</th>
                                    <th className="text-right p-1.5">Actual</th>
                                    <th className="text-right p-1.5">Variance</th>
                                    <th className="text-right p-1.5">R / ton</th>
                                    <th className="text-left p-1.5">Status</th>
                                </tr>
                            </thead>
                            <tbody>{items.map((v) => {
                                const isUnassigned = v.division === "UNASSIGNED" || !["GTRB", "RBTG"].includes(v.division);
                                return (
                                <tr key={v.vessel_id} className={`border-t border-slate-100 ${isUnassigned ? "bg-amber-50/40" : v.over_budget ? "bg-red-50/40" : "hover:bg-slate-50"}`} data-testid={`vf-row-${v.vessel_id}`}>
                                    <td className="p-1.5 pl-3 font-display font-bold text-[var(--grindrod-navy)]">{v.vessel_name}</td>
                                    <td className="p-1.5">
                                        <span className={`g-pill ${isUnassigned ? "g-pill-amber" : v.division === "RBTG" ? "g-pill-amber" : "g-pill-navy"} normal-case tracking-normal`}>{isUnassigned ? "UNASSIGNED" : v.division}</span>
                                        {canEditDivision && (
                                            <button
                                                type="button"
                                                onClick={() => setEditingVessel(v)}
                                                className="ml-1.5 text-[10px] underline text-[var(--grindrod-navy)] hover:text-[var(--grindrod-red)]"
                                                data-testid={`vf-edit-division-${v.vessel_id}`}
                                            >
                                                {isUnassigned ? "Assign" : "Edit"}
                                            </button>
                                        )}
                                    </td>
                                    <td className="p-1.5 text-slate-600">{v.client_name || "—"}</td>
                                    <td className="p-1.5 text-right text-slate-600">{Math.round(v.tonnage || 0).toLocaleString()}</td>
                                    <td className="p-1.5 text-right">{fmtZAR(v.budget_zar)}</td>
                                    <td className="p-1.5 text-right font-semibold" style={v.over_budget ? { color: "var(--grindrod-red)" } : undefined}>{fmtZAR(v.actual_zar)}</td>
                                    <td className="p-1.5 text-right font-semibold" style={{ color: v.over_budget ? "var(--grindrod-red)" : "var(--grindrod-green)" }}>{fmtZAR(v.variance_zar)}</td>
                                    <td className="p-1.5 text-right text-slate-600">{fmtZAR(v.cost_per_ton_zar)}</td>
                                    <td className="p-1.5"><span className={`g-pill ${v.over_budget ? "g-pill-red" : v.status === "completed" ? "g-pill-green" : "g-pill-amber"} normal-case tracking-normal`}>{v.over_budget ? "Over" : (v.budget_zar > 0 ? "On" : "No budget")}</span></td>
                                </tr>);
                            })}
                            </tbody>
                        </table>
                        <div className="sm:hidden divide-y divide-slate-100" data-testid="vf-cards">
                            {items.map((v) => {
                                const isUnassigned = v.division === "UNASSIGNED" || !["GTRB", "RBTG"].includes(v.division);
                                return (
                                <div key={v.vessel_id} className={`p-3 ${isUnassigned ? "bg-amber-50/40" : v.over_budget ? "bg-red-50/40" : ""}`} data-testid={`vf-card-${v.vessel_id}`}>
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <div className="font-display font-bold text-[var(--grindrod-navy)] flex-1 min-w-0">{v.vessel_name}</div>
                                        <span className={`g-pill ${isUnassigned ? "g-pill-amber" : v.division === "RBTG" ? "g-pill-amber" : "g-pill-navy"} normal-case tracking-normal`}>{isUnassigned ? "UNASSIGNED" : v.division}</span>
                                        <span className={`g-pill ${v.over_budget ? "g-pill-red" : v.budget_zar > 0 ? "g-pill-green" : "g-pill-amber"} normal-case tracking-normal`}>{v.over_budget ? "Over" : (v.budget_zar > 0 ? "On" : "No budget")}</span>
                                    </div>
                                    {canEditDivision && (
                                        <button
                                            type="button"
                                            onClick={() => setEditingVessel(v)}
                                            className="text-[11px] underline text-[var(--grindrod-navy)] mb-1.5"
                                            data-testid={`vf-edit-division-mobile-${v.vessel_id}`}
                                        >
                                            {isUnassigned ? "Assign division" : "Edit division"}
                                        </button>
                                    )}
                                    <div className="text-[11px] text-slate-500 mb-1.5">{v.client_name} · {Math.round(v.tonnage || 0).toLocaleString()} t</div>
                                    <div className="grid grid-cols-3 gap-2 text-[11px]">
                                        <div><div className="uppercase-label text-[9px]">Budget</div><div className="tabular">{fmtZAR(v.budget_zar)}</div></div>
                                        <div><div className="uppercase-label text-[9px]">Actual</div><div className="tabular font-semibold" style={v.over_budget ? { color: "var(--grindrod-red)" } : undefined}>{fmtZAR(v.actual_zar)}</div></div>
                                        <div><div className="uppercase-label text-[9px]">Variance</div><div className="tabular font-semibold" style={{ color: v.over_budget ? "var(--grindrod-red)" : "var(--grindrod-green)" }}>{fmtZAR(v.variance_zar)}</div></div>
                                    </div>
                                </div>);
                            })}
                        </div>
                    </>
                )}
            </div>
            {editingVessel && (
                <EditDivisionModal
                    vessel={editingVessel}
                    onClose={() => setEditingVessel(null)}
                    onSaved={() => { setEditingVessel(null); onRefresh && onRefresh(); }}
                />
            )}
        </div>
    );
}

function EditDivisionModal({ vessel, onClose, onSaved }) {
    const current = ["GTRB", "RBTG"].includes(vessel.division) ? vessel.division : "";
    const [division, setDivision] = useState(current);
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (!division) return;
        setSaving(true);
        try {
            await api.patch(`/vessels/${vessel.vessel_id}/division`, { division });
            toast.success(`${vessel.vessel_name} moved to ${division}`);
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update division");
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-division-modal">
            <div className="bg-white rounded-lg w-full max-w-md p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-1">
                    <Boat size={18} className="text-[var(--grindrod-navy)]" />
                    <div className="font-display font-black text-lg text-[var(--grindrod-navy)]">Reassign division</div>
                </div>
                <div className="text-sm text-slate-600 mb-3"><b>{vessel.vessel_name}</b> · {vessel.client_name || ""}</div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                    {[
                        { key: "GTRB", title: "GTRB", sub: "Grindrod Terminals Richards Bay" },
                        { key: "RBTG", title: "RBTG", sub: "Richards Bay Terminals Grindrod" },
                    ].map((d) => (
                        <button
                            key={d.key}
                            type="button"
                            data-testid={`edit-division-${d.key}`}
                            onClick={() => setDivision(d.key)}
                            className={`text-left p-3 rounded-md border-2 transition ${
                                division === d.key
                                    ? "border-[var(--grindrod-navy)] bg-[var(--grindrod-navy)]/5"
                                    : "border-slate-200 hover:border-slate-300"
                            }`}
                        >
                            <div className={`font-display font-black text-base ${division === d.key ? "text-[var(--grindrod-navy)]" : "text-slate-700"}`}>{d.title}</div>
                            <div className="text-[10.5px] text-slate-500 mt-0.5">{d.sub}</div>
                        </button>
                    ))}
                </div>
                <div className="text-[11px] text-slate-500 mb-3">Costs and budget for this vessel will roll up under the selected division on the Terminal Report.</div>
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="btn-outline" data-testid="edit-division-cancel">Cancel</button>
                    <button onClick={save} disabled={!division || saving} className="btn-primary" data-testid="edit-division-save">
                        {saving ? "Saving…" : "Save division"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function KpiCell({ label, value, sub, color, testid }) {
    return (
        <div className="bg-white px-3 py-2" data-testid={testid}>
            <div className="uppercase-label text-[9.5px] text-slate-500">{label}</div>
            <div className="font-display font-black tabular text-base" style={{ color }}>{value}</div>
            {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
        </div>
    );
}

function DivisionPanel({ d }) {
    const isUnassigned = d.division === "UNASSIGNED";
    const over = !!d.over_monthly_budget;
    const pct = d.monthly_budget_zar ? Math.min(100, Math.round((d.actual_vs_monthly_zar / d.monthly_budget_zar) * 100)) : 0;
    const pillClass = isUnassigned ? "g-pill-red" : (d.division === "GTRB" ? "g-pill-navy" : "g-pill-amber");
    const subText = isUnassigned
        ? "Vessels without a division assignment (legacy data)"
        : (d.division === "GTRB" ? "Grindrod Terminals Richards Bay" : "Richards Bay Terminals Grindrod");
    return (
        <div className={`p-3 ${isUnassigned ? "bg-amber-50" : "bg-white"}`} data-testid={`vf-division-${d.division}`}>
            <div className="flex items-center gap-2 mb-2">
                <span className={`g-pill ${pillClass} normal-case tracking-normal`}>{d.division}</span>
                <div className="text-[10px] text-slate-500 flex-1">{subText}</div>
                {over && <span className="g-pill g-pill-red normal-case tracking-normal">Over budget</span>}
            </div>
            {isUnassigned ? (
                <div className="text-[11px] text-slate-700">
                    {d.vessels_in_period} vessel{d.vessels_in_period === 1 ? "" : "s"} with costs <b className="tabular">{fmtZAR(d.vessel_actual_zar)}</b> have no division assigned.<br />
                    <span className="text-[var(--grindrod-red)] font-semibold">These costs are NOT counted toward any division's budget.</span> Use the "Assign" links below to fix.
                </div>
            ) : (
            <>
            <div className="grid grid-cols-3 gap-2 mb-2 text-[11px]">
                <div><div className="uppercase-label text-[9px]">Monthly budget</div><div className="tabular font-display font-bold text-[var(--grindrod-navy)]">{fmtZAR(d.monthly_budget_zar)}</div></div>
                <div><div className="uppercase-label text-[9px]">Actual</div><div className="tabular font-display font-bold" style={{ color: over ? "var(--grindrod-red)" : "#B36B00" }}>{fmtZAR(d.actual_vs_monthly_zar)}</div></div>
                <div><div className="uppercase-label text-[9px]">Variance</div><div className="tabular font-display font-bold" style={{ color: over ? "var(--grindrod-red)" : "var(--grindrod-green)" }}>{fmtZAR(d.variance_zar)}</div></div>
            </div>
            <div className="h-1.5 rounded-sm bg-slate-200 overflow-hidden mb-1.5">
                <div className={over ? "h-full bg-[var(--grindrod-red)]" : "h-full bg-[var(--grindrod-green)]"} style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
            <div className="text-[10.5px] text-slate-500">
                {d.vessels_in_period} vessel{d.vessels_in_period === 1 ? "" : "s"} this period
                {d.vessels_over_budget > 0 && <span className="text-[var(--grindrod-red)] font-semibold"> · {d.vessels_over_budget} over vessel-budget</span>}
                {" · "}vessel costs {fmtZAR(d.vessel_actual_zar)} of {fmtZAR(d.vessel_budget_zar)} budgeted
            </div>
            </>)}
        </div>
    );
}



/* ===================== BREAKDOWNS ===================== */
function BreakdownsCard({ data, onMoreInfo }) {
    const b = data.breakdowns || {};
    const s = data.breakdowns_summary || {};
    const manual = s.source === "manual";
    const m = b.mechanical || {};
    const e = b.electrical || {};
    return (
        <Card title="Breakdowns" Icon={Wrench} testid="breakdowns-card"
              accentBadge={manual ? { label: "Manual", color: "#1E40AF" } : { label: "Auto", color: "#64748B" }}
              onMoreInfo={onMoreInfo}>
            <table className="w-full text-xs tabular">
                <thead className="text-slate-500 uppercase text-[9.5px] tracking-wide">
                    <tr>
                        <th className="text-left py-1">Department</th>
                        <th className="text-center px-1">Mech.</th>
                        <th className="text-center px-1">Elec.</th>
                        <th className="text-center px-1">Total</th>
                    </tr>
                </thead>
                <tbody>
                    <tr className="border-t border-slate-100">
                        <td className="py-1.5 text-slate-700">Major (&gt; 4 hrs)</td>
                        <td className="text-center">{m.major || 0}</td>
                        <td className="text-center">{e.major || 0}</td>
                        <td className="text-center font-semibold">{(m.major || 0) + (e.major || 0)}</td>
                    </tr>
                    <tr className="border-t border-slate-100">
                        <td className="py-1.5 text-slate-700">Minor (≤ 4 hrs)</td>
                        <td className="text-center">{m.minor || 0}</td>
                        <td className="text-center">{e.minor || 0}</td>
                        <td className="text-center font-semibold">{(m.minor || 0) + (e.minor || 0)}</td>
                    </tr>
                    <tr className="border-t-2 border-[var(--grindrod-navy)] font-display font-bold text-[var(--grindrod-navy)]">
                        <td className="py-1.5 uppercase text-[10px]">Events</td>
                        <td className="text-center">{m.total || 0}</td>
                        <td className="text-center">{e.total || 0}</td>
                        <td className="text-center" data-testid="breakdowns-total">{s.total_events || 0}</td>
                    </tr>
                    <tr className="border-t border-slate-100 text-[var(--grindrod-red)] font-display font-black">
                        <td className="py-1.5 uppercase text-[10px]">Downtime (hrs)</td>
                        <td className="text-center">{(m.downtime_hrs || 0).toFixed(1)}</td>
                        <td className="text-center">{(e.downtime_hrs || 0).toFixed(1)}</td>
                        <td className="text-center" data-testid="breakdowns-downtime">{(s.total_downtime_hrs || 0).toFixed(1)}</td>
                    </tr>
                </tbody>
            </table>
            {(s.manual_notes?.length > 0 || data.top_delay_reasons?.length > 0) && (
                <div className="mt-3 g-card p-2 bg-slate-50 text-xs">
                    <div className="flex items-start gap-2 mb-1.5">
                        <Wrench size={13} weight="fill" className="text-[var(--grindrod-navy)] flex-shrink-0 mt-0.5" />
                        <div className="font-display font-bold text-[var(--grindrod-navy)] uppercase text-[10px]">Key Drivers</div>
                    </div>
                    <ul className="space-y-1 list-disc list-inside text-slate-700">
                        {s.manual_notes?.length > 0
                            ? s.manual_notes.slice(0, 3).map((n, i) => (
                                <li key={n.id || `${n.department}-${i}`}><span className="font-semibold capitalize">{n.department}:</span> {n.note}</li>
                            ))
                            : data.top_delay_reasons?.slice(0, 3).map((r, i) => (
                                <li key={r.reason || i}><span className="font-semibold">{r.reason}</span> · {r.events} ev · ~{Math.round(r.lost_tons)} t lost</li>
                            ))
                        }
                    </ul>
                </div>
            )}
        </Card>
    );
}

/* ===================== OVERTIME ===================== */
function OvertimeCard({ data, onMoreInfo }) {
    const o = data.overtime_by_department || {};
    const labels = { operations: "Operations", mechanical: "Mechanical", electrical: "Electrical", other: "Other" };
    const icons = { operations: Users, mechanical: Wrench, electrical: Lightning, other: ChartLineUp };
    const total_w1 = Object.values(o).reduce((a, x) => a + (x.week_1 || 0), 0);
    const total_w2 = Object.values(o).reduce((a, x) => a + (x.week_2 || 0), 0);
    const total_all = total_w1 + total_w2;
    return (
        <Card title="Overtime Hours" Icon={Clock} testid="overtime-card" onMoreInfo={onMoreInfo}>
            <table className="w-full text-xs tabular">
                <thead className="text-slate-500 uppercase text-[9.5px] tracking-wide">
                    <tr>
                        <th className="text-left py-1">Dept.</th>
                        <th className="text-center px-1">Wk 1</th>
                        <th className="text-center px-1">Wk 2</th>
                        <th className="text-right px-1">Total</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(o).map(([k, v]) => {
                        const I = icons[k] || ChartLineUp;
                        return (
                            <tr key={k} className="border-t border-slate-100">
                                <td className="py-1.5 text-slate-700 inline-flex items-center gap-1.5"><I size={12} className="text-slate-500" /> {labels[k]}</td>
                                <td className="text-center">{v.week_1?.toFixed(1)}</td>
                                <td className="text-center">{v.week_2?.toFixed(1)}</td>
                                <td className="text-right font-semibold">{v.total?.toFixed(1)}</td>
                            </tr>
                        );
                    })}
                    <tr className="border-t-2 border-[var(--grindrod-navy)] font-display font-bold text-[var(--grindrod-navy)]">
                        <td className="py-1.5 uppercase text-[10px]">Total</td>
                        <td className="text-center">{total_w1.toFixed(1)}</td>
                        <td className="text-center">{total_w2.toFixed(1)}</td>
                        <td className="text-right" data-testid="overtime-total">{total_all.toFixed(1)}</td>
                    </tr>
                </tbody>
            </table>
        </Card>
    );
}

/* ===================== LABOUR / PLANT HIRE ===================== */
function LabourCard({ data, onMoreInfo }) {
    const al = data.additional_labour || [];
    const ph = data.plant_hire || [];
    const typeLabel = (k) => ({
        general_workers: "General Workers", fitters: "Fitters", electricians: "Electricians",
        front_end_loader: "Front-end Loader", mobile_excavator: "Mobile Excavator", other: "Other",
    }[k] || k);
    return (
        <Card title="Labour / Plant Hire" Icon={UserPlus} testid="labour-card" onMoreInfo={onMoreInfo}>
            <div className="text-[10px] uppercase font-display font-bold text-slate-500 mb-1">Additional Labour</div>
            {al.length === 0 ? (
                <div className="text-[11px] text-slate-400 mb-2">No additional labour logged.</div>
            ) : (
                <table className="w-full text-xs tabular mb-3">
                    <thead className="text-slate-500 uppercase text-[9.5px] tracking-wide">
                        <tr><th className="text-left py-1">Type</th><th className="text-center px-1">Wk 1</th><th className="text-center px-1">Wk 2</th></tr>
                    </thead>
                    <tbody>
                        {al.map((l, i) => (
                            <tr key={l.type || i} className="border-t border-slate-100"><td className="py-1.5 text-slate-700">{typeLabel(l.type)}</td><td className="text-center">{l.week_1}</td><td className="text-center">{l.week_2}</td></tr>
                        ))}
                    </tbody>
                </table>
            )}
            <div className="text-[10px] uppercase font-display font-bold text-slate-500 mb-1 inline-flex items-center gap-1.5"><Truck size={11} /> Plant Hire</div>
            {ph.length === 0 ? (
                <div className="text-[11px] text-slate-400">No plant hire logged.</div>
            ) : (
                <table className="w-full text-xs tabular">
                    <thead className="text-slate-500 uppercase text-[9.5px] tracking-wide">
                        <tr><th className="text-left py-1">Equipment</th><th className="text-center px-1">Wk 1</th><th className="text-center px-1">Wk 2</th></tr>
                    </thead>
                    <tbody>
                        {ph.map((l, i) => (
                            <tr key={l.type || i} className="border-t border-slate-100"><td className="py-1.5 text-slate-700">{typeLabel(l.type)}</td><td className="text-center">{l.week_1}</td><td className="text-center">{l.week_2}</td></tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Card>
    );
}

/* ===================== FINANCE ===================== */
function FinanceCard({ data, onMoreInfo }) {
    const f = data.finance || { entries: [] };
    return (
        <Card title="Finance Summary" Icon={CurrencyDollar} testid="finance-card" onMoreInfo={onMoreInfo}>
            {f.entries.length === 0 ? (
                <div className="text-xs text-slate-400 py-2">No finance entries for this period.</div>
            ) : (
                <table className="w-full text-xs tabular">
                    <thead className="text-slate-500 uppercase text-[9.5px] tracking-wide">
                        <tr>
                            <th className="text-left py-1">Description</th>
                            <th className="text-right px-1">Budget</th>
                            <th className="text-right px-1">Actual</th>
                            <th className="text-right px-1">Var %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {f.entries.map((e, i) => {
                            const variance = e.budget_zar - e.actual_zar;
                            const pct = e.budget_zar ? (variance / e.budget_zar) * 100 : 0;
                            const fav = variance >= 0;
                            return (
                                <tr key={e.description || i} className="border-t border-slate-100">
                                    <td className="py-1.5 text-slate-700">{e.description}</td>
                                    <td className="text-right">{fmtZAR(e.budget_zar)}</td>
                                    <td className="text-right">{fmtZAR(e.actual_zar)}</td>
                                    <td className={`text-right font-semibold ${fav ? "text-[var(--grindrod-green)]" : "text-[var(--grindrod-red)]"}`}>{(fav ? "" : "+")}{Math.abs(pct).toFixed(1)}%</td>
                                </tr>
                            );
                        })}
                        <tr className="border-t-2 border-[var(--grindrod-navy)] font-display font-bold text-[var(--grindrod-navy)]">
                            <td className="py-1.5 uppercase text-[10px]">Total (ZAR)</td>
                            <td className="text-right">{fmtZAR(f.total_budget_zar)}</td>
                            <td className="text-right">{fmtZAR(f.total_actual_zar)}</td>
                            <td className={`text-right ${f.variance_zar >= 0 ? "text-[var(--grindrod-green)]" : "text-[var(--grindrod-red)]"}`} data-testid="finance-variance-pct">{f.variance_zar >= 0 ? "" : "+"}{Math.abs(f.variance_pct).toFixed(1)}%</td>
                        </tr>
                    </tbody>
                </table>
            )}
            <div className="text-[10px] text-slate-400 mt-2">All amounts in ZAR.</div>
        </Card>
    );
}

/* ===================== BULLET LIST CARDS ===================== */
function BulletCard({ title, Icon, accent, items, testid }) {
    return (
        <Card title={title} Icon={Icon} accent={accent} testid={testid}>
            {(!items || items.length === 0) ? (
                <div className="text-xs text-slate-400 py-2">No items.</div>
            ) : (
                <ul className="space-y-2 text-[12.5px] text-slate-700" data-testid={`${testid}-list`}>
                    {items.map((it, i) => (
                        <li key={typeof it === "string" ? `${it.slice(0, 40)}-${i}` : i} className="flex items-start gap-2">
                            <CheckCircle size={14} weight="fill" style={{ color: accent }} className="flex-shrink-0 mt-0.5" />
                            <span>{it}</span>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
}

/* ===================== Card shell ===================== */
function Card({ title, Icon, accent = "var(--grindrod-navy)", testid, children, accentBadge, onMoreInfo }) {
    return (
        <div className="g-card overflow-hidden h-full flex flex-col" data-testid={testid}>
            <div className="px-3.5 py-2.5 text-white flex items-center gap-2 font-display font-black uppercase tracking-wide text-[12px]" style={{ background: accent }}>
                <Icon size={14} weight="fill" />
                {title}
                {accentBadge && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold"
                          style={{ background: accentBadge.color, color: "white" }}
                          data-testid="card-source-badge">
                        {accentBadge.label}
                    </span>
                )}
                {onMoreInfo && (
                    <button
                        onClick={onMoreInfo}
                        className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-white/15 hover:bg-white/30 transition font-semibold"
                        data-testid={`${testid}-more-info`}
                        title="See the actual records behind this card"
                    >
                        <Info size={11} weight="fill" /> More info
                    </button>
                )}
            </div>
            <div className="p-3.5 flex-1">{children}</div>
        </div>
    );
}

/* ===================== MODALS ===================== */

function ModalShell({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="g-card w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="g-card-header-dark"><div className="font-display font-black uppercase tracking-wide">{title}</div></div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
}

function ComplaintModal({ onClose, onSaved }) {
    const [f, setF] = useState({ category: "delays_turnaround", description: "", client_name: "", vessel_name: "", operation: "rb_vessel" });
    const submit = async () => {
        try {
            await api.post("/complaints", f);
            toast.success("Complaint logged");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to log"); }
    };
    return (
        <ModalShell title="Log Client Complaint" onClose={onClose}>
            <div className="space-y-3 text-sm">
                <div><label className="uppercase-label block mb-1">Category</label>
                    <select className="g-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="complaint-category">
                        <option value="delays_turnaround">Delays / Turnaround Time</option>
                        <option value="communication">Communication</option>
                        <option value="documentation">Documentation</option>
                        <option value="cargo_condition">Cargo Condition / Damage</option>
                        <option value="other">Other</option>
                    </select>
                </div>
                <div><label className="uppercase-label block mb-1">Client</label><input className="g-input" value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} /></div>
                <div><label className="uppercase-label block mb-1">Vessel (optional)</label><input className="g-input" value={f.vessel_name} onChange={(e) => setF({ ...f, vessel_name: e.target.value })} /></div>
                <div><label className="uppercase-label block mb-1">Description *</label><textarea className="g-input" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="complaint-description" /></div>
                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="btn-outline">Cancel</button>
                    <button onClick={submit} className="btn-primary" disabled={!f.description.trim()} data-testid="complaint-submit">Log Complaint</button>
                </div>
            </div>
        </ModalShell>
    );
}

function SafetyModal({ onClose, onSaved }) {
    const [f, setF] = useState({ incident_type: "near_miss", description: "", operation: "terminal", hours_lost: 0 });
    const submit = async () => {
        try {
            await api.post("/safety/incidents", f);
            toast.success("Safety incident logged");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to log"); }
    };
    return (
        <ModalShell title="Log Safety Incident" onClose={onClose}>
            <div className="space-y-3 text-sm">
                <div><label className="uppercase-label block mb-1">Type</label>
                    <select className="g-input" value={f.incident_type} onChange={(e) => setF({ ...f, incident_type: e.target.value })} data-testid="safety-type">
                        <option value="near_miss">Near miss</option>
                        <option value="first_aid">First aid</option>
                        <option value="medical">Medical treatment</option>
                        <option value="lost_time">Lost time injury (LTIFR)</option>
                        <option value="property_damage">Property damage</option>
                    </select>
                </div>
                <div><label className="uppercase-label block mb-1">Description *</label><textarea className="g-input" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="safety-description" /></div>
                <div><label className="uppercase-label block mb-1">Hours lost</label><input type="number" min="0" step="0.5" className="g-input" value={f.hours_lost} onChange={(e) => setF({ ...f, hours_lost: parseFloat(e.target.value) || 0 })} /></div>
                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="btn-outline">Cancel</button>
                    <button onClick={submit} className="btn-primary" disabled={!f.description.trim()} data-testid="safety-submit">Log Incident</button>
                </div>
            </div>
        </ModalShell>
    );
}

function BreakdownModal({ onClose, onSaved }) {
    const today = new Date(); today.setDate(today.getDate() - today.getDay() + 1); // monday
    const [f, setF] = useState({
        department: "mechanical",
        week_start: today.toISOString().slice(0, 10),
        major_count: 0,
        minor_count: 0,
        downtime_hrs: 0,
        operation: "terminal",
        notes: "",
    });
    const submit = async () => {
        try {
            await api.post("/breakdowns", f);
            toast.success("Breakdown logged");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    };
    return (
        <ModalShell title="Log Breakdown (Planner / Electrical Line Mgr)" onClose={onClose}>
            <div className="space-y-3 text-sm">
                <div><label className="uppercase-label block mb-1">Department</label>
                    <select className="g-input" value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} data-testid="breakdown-dept">
                        <option value="mechanical">Mechanical</option>
                        <option value="electrical">Electrical</option>
                    </select>
                </div>
                <div><label className="uppercase-label block mb-1">Week starting (Mon)</label>
                    <input type="date" className="g-input" value={f.week_start} onChange={(e) => setF({ ...f, week_start: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div><label className="uppercase-label block mb-1">Major (&gt; 4 hrs)</label>
                        <input type="number" min="0" step="1" className="g-input tabular" value={f.major_count} onChange={(e) => setF({ ...f, major_count: parseInt(e.target.value) || 0 })} data-testid="breakdown-major" />
                    </div>
                    <div><label className="uppercase-label block mb-1">Minor (≤ 4 hrs)</label>
                        <input type="number" min="0" step="1" className="g-input tabular" value={f.minor_count} onChange={(e) => setF({ ...f, minor_count: parseInt(e.target.value) || 0 })} data-testid="breakdown-minor" />
                    </div>
                </div>
                <div><label className="uppercase-label block mb-1">Downtime (hrs)</label>
                    <input type="number" min="0" step="0.1" className="g-input tabular" value={f.downtime_hrs} onChange={(e) => setF({ ...f, downtime_hrs: parseFloat(e.target.value) || 0 })} data-testid="breakdown-downtime" />
                </div>
                <div><label className="uppercase-label block mb-1">Notes (key driver — e.g. "Engine auxiliaries, conveyor systems")</label>
                    <input className="g-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Short summary used as a Key Driver bullet" />
                </div>
                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="btn-outline">Cancel</button>
                    <button onClick={submit} className="btn-primary" data-testid="breakdown-submit">Save</button>
                </div>
            </div>
        </ModalShell>
    );
}

function OvertimeModal({ onClose, onSaved }) {
    const today = new Date(); today.setDate(today.getDate() - today.getDay() + 1); // monday
    const [f, setF] = useState({ department: "operations", week_start: today.toISOString().slice(0, 10), hours: 0, operation: "terminal", notes: "" });
    const submit = async () => {
        try {
            await api.post("/overtime", f);
            toast.success("Overtime saved");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    };
    return (
        <ModalShell title="Log Overtime Hours" onClose={onClose}>
            <div className="space-y-3 text-sm">
                <div><label className="uppercase-label block mb-1">Department</label>
                    <select className="g-input" value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} data-testid="overtime-dept">
                        <option value="operations">Operations</option>
                        <option value="mechanical">Mechanical</option>
                        <option value="electrical">Electrical</option>
                        <option value="other">Other</option>
                    </select>
                </div>
                <div><label className="uppercase-label block mb-1">Week starting (Mon)</label><input type="date" className="g-input" value={f.week_start} onChange={(e) => setF({ ...f, week_start: e.target.value })} /></div>
                <div><label className="uppercase-label block mb-1">Hours</label><input type="number" min="0" step="0.5" className="g-input" value={f.hours} onChange={(e) => setF({ ...f, hours: parseFloat(e.target.value) || 0 })} data-testid="overtime-hours" /></div>
                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="btn-outline">Cancel</button>
                    <button onClick={submit} className="btn-primary" data-testid="overtime-submit">Save</button>
                </div>
            </div>
        </ModalShell>
    );
}

function LabourModal({ onClose, onSaved }) {
    const today = new Date(); today.setDate(today.getDate() - today.getDay() + 1);
    const [f, setF] = useState({ type: "general_workers", week_start: today.toISOString().slice(0, 10), units: 0, is_plant_hire: false, notes: "", operation: "terminal" });
    const submit = async () => {
        try {
            await api.post("/labour", f);
            toast.success("Labour entry saved");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    };
    return (
        <ModalShell title="Log Labour / Plant Hire" onClose={onClose}>
            <div className="space-y-3 text-sm">
                <div><label className="uppercase-label block mb-1">Type</label>
                    <select className="g-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value, is_plant_hire: ["front_end_loader", "mobile_excavator"].includes(e.target.value) })} data-testid="labour-type">
                        <optgroup label="Labour">
                            <option value="general_workers">General Workers</option>
                            <option value="fitters">Fitters</option>
                            <option value="electricians">Electricians</option>
                        </optgroup>
                        <optgroup label="Plant Hire">
                            <option value="front_end_loader">Front-end Loader</option>
                            <option value="mobile_excavator">Mobile Excavator</option>
                        </optgroup>
                        <option value="other">Other</option>
                    </select>
                </div>
                <div><label className="uppercase-label block mb-1">Week starting (Mon)</label><input type="date" className="g-input" value={f.week_start} onChange={(e) => setF({ ...f, week_start: e.target.value })} /></div>
                <div><label className="uppercase-label block mb-1">{f.is_plant_hire ? "Units / count" : "Peak days / headcount"}</label><input type="number" min="0" step="1" className="g-input" value={f.units} onChange={(e) => setF({ ...f, units: parseFloat(e.target.value) || 0 })} data-testid="labour-units" /></div>
                <div><label className="uppercase-label block mb-1">Notes</label><input className="g-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="btn-outline">Cancel</button>
                    <button onClick={submit} className="btn-primary" data-testid="labour-submit">Save</button>
                </div>
            </div>
        </ModalShell>
    );
}

function FinanceModal({ periodLabel, onClose, onSaved }) {
    const [f, setF] = useState({ description: "", period_label: periodLabel, budget_zar: 0, actual_zar: 0, operation: "terminal", division: "GTRB" });
    const submit = async () => {
        try {
            await api.post("/finance/entries", f);
            toast.success("Finance entry saved");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    };
    return (
        <ModalShell title="Log Finance Entry" onClose={onClose}>
            <div className="space-y-3 text-sm">
                <div>
                    <label className="uppercase-label block mb-1">Division *</label>
                    <div className="grid grid-cols-2 gap-2" data-testid="finance-division">
                        {["GTRB", "RBTG"].map((d) => (
                            <button key={d} type="button" onClick={() => setF({ ...f, division: d })}
                                    data-testid={`finance-division-${d}`}
                                    className={`px-3 py-2 rounded-sm border font-display font-bold uppercase tracking-wide text-sm transition-colors ${
                                        f.division === d
                                            ? "border-[var(--grindrod-red)] bg-[var(--grindrod-red)] text-white"
                                            : "border-slate-300 bg-white text-slate-600 hover:border-[var(--grindrod-navy)]"
                                    }`}>
                                {d}
                                <div className="text-[9.5px] font-normal normal-case tracking-normal opacity-80">
                                    {d === "GTRB" ? "Grindrod Terminals Richards Bay" : "Richards Bay Terminals Grindrod"}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
                <div><label className="uppercase-label block mb-1">Description *</label><input className="g-input" placeholder="e.g. Vessel Operating Costs" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="finance-desc" /></div>
                <div><label className="uppercase-label block mb-1">Period label</label><input className="g-input" value={f.period_label} onChange={(e) => setF({ ...f, period_label: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                    <div><label className="uppercase-label block mb-1">Budget (ZAR)</label><input type="number" className="g-input tabular" value={f.budget_zar} onChange={(e) => setF({ ...f, budget_zar: parseFloat(e.target.value) || 0 })} data-testid="finance-budget" /></div>
                    <div><label className="uppercase-label block mb-1">Actual (ZAR)</label><input type="number" className="g-input tabular" value={f.actual_zar} onChange={(e) => setF({ ...f, actual_zar: parseFloat(e.target.value) || 0 })} data-testid="finance-actual" /></div>
                </div>
                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="btn-outline">Cancel</button>
                    <button onClick={submit} className="btn-primary" disabled={!f.description.trim()} data-testid="finance-submit">Save</button>
                </div>
            </div>
        </ModalShell>
    );
}

function TargetModal({ periodLabel, operation, onClose, onSaved }) {
    const DIVISIONS = [
        { k: "TERMINAL", label: "Terminal-wide", help: "Combined target across GTRB and RBTG", colour: "var(--grindrod-navy)" },
        { k: "GTRB",     label: "GTRB",          help: "Grindrod Terminals Richards Bay",     colour: "var(--grindrod-navy)" },
        { k: "RBTG",     label: "RBTG",          help: "Richards Bay Terminals Group",         colour: "#B36B00" },
    ];
    const [existing, setExisting] = useState([]);  // /terminal-targets?period_label=
    const [division, setDivision] = useState("TERMINAL");
    const [f, setF] = useState({
        period_label: periodLabel,
        operation,
        target_tons: 0,
        target_voyages: 0,
        target_on_time_pct: 95,
        target_turnaround_hrs: 30,
        vessel_name: "",
    });

    // Load any existing target for the chosen division so the form pre-fills
    useEffect(() => {
        let cancelled = false;
        api.get("/terminal-targets", { params: { period_label: f.period_label } })
            .then((r) => { if (!cancelled) setExisting(r.data || []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [f.period_label]);

    useEffect(() => {
        const t = existing.find((x) => !x.vessel_name && (x.division || "TERMINAL") === division);
        if (t) {
            setF((prev) => ({
                ...prev,
                target_tons: t.target_tons || 0,
                target_voyages: t.target_voyages || 0,
                target_on_time_pct: t.target_on_time_pct || 95,
                target_turnaround_hrs: t.target_turnaround_hrs || 30,
                vessel_name: t.vessel_name || "",
            }));
        } else {
            setF((prev) => ({ ...prev, target_tons: 0, target_voyages: 0, target_on_time_pct: 95, target_turnaround_hrs: 30, vessel_name: "" }));
        }
    }, [division, existing]);

    const submit = async () => {
        try {
            await api.post("/terminal-targets", {
                ...f,
                division,
                vessel_name: f.vessel_name || null,
            });
            toast.success(`${division === "TERMINAL" ? "Terminal-wide" : division} targets saved for ${f.period_label}`);
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    };

    const activeDiv = DIVISIONS.find((d) => d.k === division);

    return (
        <ModalShell title="Set Performance Targets" onClose={onClose}>
            <div className="space-y-3 text-sm">
                {/* Division segmented control */}
                <div>
                    <label className="uppercase-label block mb-1.5">Division</label>
                    <div className="grid grid-cols-3 gap-1.5" data-testid="target-division-tabs">
                        {DIVISIONS.map((d) => (
                            <button
                                key={d.k}
                                onClick={() => setDivision(d.k)}
                                data-testid={`target-division-${d.k}`}
                                className={`px-2 py-2 rounded-sm text-[11px] font-display font-bold uppercase tracking-wide border-2 transition ${
                                    division === d.k
                                        ? "text-white border-current"
                                        : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                                }`}
                                style={division === d.k ? { background: d.colour, borderColor: d.colour } : {}}
                            >
                                {d.label}
                            </button>
                        ))}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1" data-testid="target-division-help">
                        {activeDiv?.help}
                    </div>
                </div>

                <div>
                    <label className="uppercase-label block mb-1">Period</label>
                    <input className="g-input" value={f.period_label} onChange={(e) => setF({ ...f, period_label: e.target.value })} data-testid="target-period" />
                </div>
                <div>
                    <label className="uppercase-label block mb-1">Vessel (optional — leave blank for division-wide)</label>
                    <input className="g-input" value={f.vessel_name} onChange={(e) => setF({ ...f, vessel_name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div><label className="uppercase-label block mb-1">Target Tons</label><input type="number" className="g-input tabular" value={f.target_tons} onChange={(e) => setF({ ...f, target_tons: parseFloat(e.target.value) || 0 })} data-testid="target-tons" /></div>
                    <div><label className="uppercase-label block mb-1">Target Voyages</label><input type="number" className="g-input tabular" value={f.target_voyages} onChange={(e) => setF({ ...f, target_voyages: parseInt(e.target.value) || 0 })} /></div>
                    <div><label className="uppercase-label block mb-1">On-Time %</label><input type="number" className="g-input tabular" value={f.target_on_time_pct} onChange={(e) => setF({ ...f, target_on_time_pct: parseFloat(e.target.value) || 0 })} /></div>
                    <div><label className="uppercase-label block mb-1">Turnaround (hrs)</label><input type="number" className="g-input tabular" value={f.target_turnaround_hrs} onChange={(e) => setF({ ...f, target_turnaround_hrs: parseFloat(e.target.value) || 0 })} /></div>
                </div>

                {/* Per-division snapshot */}
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-2 text-[11px]" data-testid="target-existing">
                    <div className="uppercase-label text-slate-500 mb-1">Existing targets for {f.period_label}</div>
                    {existing.filter((x) => !x.vessel_name).length === 0 ? (
                        <div className="text-slate-400 italic">None yet — set the first one.</div>
                    ) : (
                        <ul className="space-y-0.5">
                            {existing.filter((x) => !x.vessel_name).map((t) => (
                                <li key={t.id} className="text-slate-600">
                                    <b className="text-[var(--grindrod-navy)]">{t.division || "TERMINAL"}</b>
                                    <span> · tons {Number(t.target_tons || 0).toLocaleString()} · voy {t.target_voyages || 0} · on-time {t.target_on_time_pct || 0}% · turn {t.target_turnaround_hrs || 0}h</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="btn-outline">Cancel</button>
                    <button onClick={submit} className="btn-primary" data-testid="target-submit">Save {activeDiv?.label} Targets</button>
                </div>
            </div>
        </ModalShell>
    );
}


/* ===================== MANAGE ENTRIES MODAL =====================
   Generic list/delete modal for past entries — one per data kind. */
const MANAGE_CONFIG = {
    complaint: {
        title: "Manage client complaints",
        listUrl: "/complaints",
        deleteUrl: (id) => `/complaints/${id}`,
        useDateRange: true,
        columns: [
            { label: "When",        get: (r) => dateLabel(r.occurred_at) },
            { label: "Category",    get: (r) => (r.category || "").replace(/_/g, " ") },
            { label: "Client",      get: (r) => r.client_name || "—" },
            { label: "Vessel",      get: (r) => r.vessel_name || "—" },
            { label: "Description", get: (r) => r.description || "—", wide: true },
        ],
    },
    safety: {
        title: "Manage safety incidents",
        listUrl: "/safety/incidents",
        deleteUrl: (id) => `/safety/incidents/${id}`,
        useDateRange: true,
        columns: [
            { label: "When",        get: (r) => dateLabel(r.occurred_at) },
            { label: "Type",        get: (r) => (r.incident_type || "").replace(/_/g, " ") },
            { label: "Description", get: (r) => r.description || "—", wide: true },
            { label: "Hrs lost",    get: (r) => Number(r.hours_lost || 0).toFixed(1), align: "right" },
        ],
    },
    overtime: {
        title: "Manage overtime entries",
        listUrl: "/overtime",
        deleteUrl: (id) => `/overtime/${id}`,
        useDateRange: true,
        columns: [
            { label: "Week start", get: (r) => r.week_start },
            { label: "Department", get: (r) => r.department },
            { label: "Hours",      get: (r) => Number(r.hours || 0).toFixed(1), align: "right" },
            { label: "Notes",      get: (r) => r.notes || "—", wide: true },
        ],
    },
    breakdown: {
        title: "Manage breakdown entries",
        listUrl: "/breakdowns",
        deleteUrl: (id) => `/breakdowns/${id}`,
        useDateRange: true,
        columns: [
            { label: "Week start", get: (r) => r.week_start },
            { label: "Department", get: (r) => r.department },
            { label: "Major",      get: (r) => r.major_count, align: "right" },
            { label: "Minor",      get: (r) => r.minor_count, align: "right" },
            { label: "Downtime h", get: (r) => Number(r.downtime_hrs || 0).toFixed(1), align: "right" },
            { label: "Notes",      get: (r) => r.notes || "—", wide: true },
        ],
    },
    labour: {
        title: "Manage labour / plant hire entries",
        listUrl: "/labour",
        deleteUrl: (id) => `/labour/${id}`,
        useDateRange: true,
        columns: [
            { label: "Week start", get: (r) => r.week_start },
            { label: "Type",       get: (r) => (r.type || "").replace(/_/g, " ") },
            { label: "Units",      get: (r) => r.units, align: "right" },
            { label: "Notes",      get: (r) => r.notes || "—", wide: true },
        ],
    },
    finance: {
        title: "Manage finance entries",
        listUrl: "/finance/entries",
        deleteUrl: (id) => `/finance/entries/${id}`,
        usePeriodLabel: true,
        columns: [
            { label: "Description",   get: (r) => r.description, wide: true },
            { label: "Period",        get: (r) => r.period_label },
            { label: "Budget (ZAR)",  get: (r) => fmtZAR(r.budget_zar), align: "right" },
            { label: "Actual (ZAR)",  get: (r) => fmtZAR(r.actual_zar), align: "right" },
        ],
    },
    target: {
        title: "Manage performance targets",
        listUrl: "/terminal-targets",
        deleteUrl: (id) => `/terminal-targets/${id}`,
        usePeriodLabel: true,
        columns: [
            { label: "Period",     get: (r) => r.period_label },
            { label: "Vessel",     get: (r) => r.vessel_name || "Terminal-wide" },
            { label: "Tons",       get: (r) => fmtTons(r.target_tons), align: "right" },
            { label: "Voyages",    get: (r) => r.target_voyages, align: "right" },
            { label: "On-time %",  get: (r) => fmtPct(r.target_on_time_pct), align: "right" },
            { label: "Turnaround", get: (r) => `${fmtHrs(r.target_turnaround_hrs)} h`, align: "right" },
        ],
    },
};

function ManageEntriesModal({ kind, periodLabel, startDate, endDate, onClose, onChanged }) {
    const cfg = MANAGE_CONFIG[kind];
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [allPeriods, setAllPeriods] = useState(false); // toggle to view ALL history

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (!allPeriods) {
                if (cfg.useDateRange) {
                    if (startDate) params.start = startDate;
                    if (endDate)   params.end   = endDate;
                }
                if (cfg.usePeriodLabel && periodLabel) {
                    params.period_label = periodLabel;
                }
            }
            const r = await api.get(cfg.listUrl, { params });
            setRows(r.data || []);
        } catch {
            toast.error("Failed to load entries");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, allPeriods, startDate, endDate, periodLabel]);

    const remove = async (id) => {
        if (!window.confirm("Delete this entry permanently? This cannot be undone.")) return;
        try {
            await api.delete(cfg.deleteUrl(id));
            toast.success("Entry deleted");
            setRows((rs) => rs.filter((r) => r.id !== id));
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not delete");
        }
    };

    if (!cfg) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
            <div className="g-card w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid={`manage-modal-${kind}`}>
                <div className="g-card-header-dark">
                    <ListIcon />
                    <div className="font-display font-black uppercase tracking-wide">{cfg.title}</div>
                    <button onClick={onClose} className="ml-auto text-white text-lg leading-none" data-testid="manage-modal-close" aria-label="Close">×</button>
                </div>
                <div className="px-3 sm:px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="text-slate-500">Scope:</span>
                    <button onClick={() => setAllPeriods(false)} data-testid="manage-scope-period" className={`px-2.5 py-1 rounded-sm border font-display font-bold uppercase tracking-wide ${!allPeriods ? "bg-[var(--grindrod-navy)] text-white border-[var(--grindrod-navy)]" : "bg-white text-slate-600 border-slate-300"}`}>
                        Current period
                    </button>
                    <button onClick={() => setAllPeriods(true)} data-testid="manage-scope-all" className={`px-2.5 py-1 rounded-sm border font-display font-bold uppercase tracking-wide ${allPeriods ? "bg-[var(--grindrod-navy)] text-white border-[var(--grindrod-navy)]" : "bg-white text-slate-600 border-slate-300"}`}>
                        All history
                    </button>
                    <span className="ml-auto text-slate-500 tabular" data-testid="manage-row-count">{rows.length} entr{rows.length === 1 ? "y" : "ies"}</span>
                </div>
                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
                    ) : rows.length === 0 ? (
                        <div className="p-12 text-center text-sm text-slate-400" data-testid="manage-empty">
                            No entries {allPeriods ? "logged yet" : "in this period"}.
                        </div>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <table className="w-full text-xs tabular hidden sm:table" data-testid="manage-table">
                                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] sticky top-0">
                                    <tr>
                                        {cfg.columns.map((c) => (
                                            <th key={c.label} className={`p-2 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>
                                        ))}
                                        <th className="p-2 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`manage-row-${r.id}`}>
                                            {cfg.columns.map((c) => (
                                                <td key={c.label} className={`p-2 ${c.align === "right" ? "text-right" : "text-left"} ${c.wide ? "max-w-xs truncate" : ""}`} title={c.wide ? String(c.get(r)) : undefined}>
                                                    {c.get(r)}
                                                </td>
                                            ))}
                                            <td className="p-2 text-right">
                                                <button onClick={() => remove(r.id)} data-testid={`manage-delete-${r.id}`}
                                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[var(--grindrod-red)] text-[var(--grindrod-red)] hover:bg-[var(--grindrod-red)] hover:text-white transition-colors text-[10px] font-display font-bold uppercase tracking-wide">
                                                    <TrashIcon /> Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Mobile card list */}
                            <div className="sm:hidden divide-y divide-slate-100" data-testid="manage-cards">
                                {rows.map((r) => (
                                    <div key={r.id} className="p-3" data-testid={`manage-card-${r.id}`}>
                                        <div className="space-y-1">
                                            {cfg.columns.map((c) => (
                                                <div key={c.label} className="flex gap-2 text-[12px]">
                                                    <div className="text-slate-500 uppercase tracking-wider text-[9.5px] font-display font-bold w-24 flex-shrink-0 pt-0.5">{c.label}</div>
                                                    <div className={`flex-1 ${c.wide ? "" : "tabular"}`}>{c.get(r)}</div>
                                                </div>
                                            ))}
                                        </div>
                                        <button onClick={() => remove(r.id)} data-testid={`manage-delete-${r.id}`}
                                                className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[var(--grindrod-red)] text-[var(--grindrod-red)] hover:bg-[var(--grindrod-red)] hover:text-white transition-colors text-[11px] font-display font-bold uppercase tracking-wide">
                                            <TrashIcon /> Delete entry
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <div className="px-3 sm:px-4 py-2 border-t border-slate-200 text-[10.5px] text-slate-500 italic">
                    Tip: only the user roles allowed to create an entry can delete it (admin / manager always can).
                </div>
            </div>
        </div>
    );
}


/* ===================== DETAIL DRILL MODAL — "More info" =====================
   Read-only drill-down for every report card. Fetches the underlying records
   via the same endpoints as the Manage modal (so managers can see exactly
   what's behind the headline number) but with the Delete column hidden and
   a couple of section-specific bespoke views (executive summary, trends,
   vessel performance). */
function DetailDrillModal({ kind, data, periodLabel, startDate, endDate, onClose }) {
    // Section-specific "explanation" views first
    if (kind === "executive_summary") return <ExecSummaryDetail data={data} onClose={onClose} />;
    if (kind === "trends")            return <TrendsDetail data={data} onClose={onClose} />;
    if (kind === "vessel_performance") return <VesselPerformanceDetail data={data} onClose={onClose} />;

    const cfg = MANAGE_CONFIG[kind];
    if (!cfg) return null;
    return <DetailListView kind={kind} cfg={cfg} periodLabel={periodLabel} startDate={startDate} endDate={endDate} onClose={onClose} />;
}

function DetailListView({ kind, cfg, periodLabel, startDate, endDate, onClose }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [allPeriods, setAllPeriods] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (!allPeriods) {
                if (cfg.useDateRange) {
                    if (startDate) params.start = startDate;
                    if (endDate) params.end = endDate;
                }
                if (cfg.usePeriodLabel && periodLabel) {
                    params.period_label = periodLabel;
                }
            }
            const r = await api.get(cfg.listUrl, { params });
            setRows(r.data || []);
        } catch {
            toast.error("Failed to load detail");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, allPeriods]);

    const titleByKind = {
        complaint: "All client complaints in this period",
        safety: "All safety incidents in this period",
        overtime: "All overtime entries in this period",
        breakdown: "All breakdown entries in this period",
        labour: "All labour / plant hire entries in this period",
        finance: "All finance entries in this period",
        target: "All performance targets in this period",
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
            <div className="g-card w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid={`detail-modal-${kind}`}>
                <div className="g-card-header-dark">
                    <Info size={14} weight="fill" />
                    <div className="font-display font-black uppercase tracking-wide">{titleByKind[kind] || cfg.title}</div>
                    <button onClick={onClose} className="ml-auto text-white text-lg leading-none" data-testid="detail-modal-close" aria-label="Close">×</button>
                </div>
                <div className="px-3 sm:px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="text-slate-500">Scope:</span>
                    <button onClick={() => setAllPeriods(false)} data-testid="detail-scope-period" className={`px-2.5 py-1 rounded-sm border font-display font-bold uppercase tracking-wide ${!allPeriods ? "bg-[var(--grindrod-navy)] text-white border-[var(--grindrod-navy)]" : "bg-white text-slate-600 border-slate-300"}`}>
                        Current period
                    </button>
                    <button onClick={() => setAllPeriods(true)} data-testid="detail-scope-all" className={`px-2.5 py-1 rounded-sm border font-display font-bold uppercase tracking-wide ${allPeriods ? "bg-[var(--grindrod-navy)] text-white border-[var(--grindrod-navy)]" : "bg-white text-slate-600 border-slate-300"}`}>
                        All history
                    </button>
                    <span className="ml-auto text-slate-500 tabular" data-testid="detail-row-count">{rows.length} record{rows.length === 1 ? "" : "s"}</span>
                </div>
                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
                    ) : rows.length === 0 ? (
                        <div className="p-12 text-center text-sm text-slate-400" data-testid="detail-empty">
                            No records {allPeriods ? "logged yet" : "in this period"}.
                        </div>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <table className="w-full text-xs tabular hidden sm:table" data-testid="detail-table">
                                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] sticky top-0">
                                    <tr>
                                        {cfg.columns.map((c) => (
                                            <th key={c.label} className={`p-2 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>
                                        ))}
                                        <th className="p-2 text-left">Logged by</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`detail-row-${r.id}`}>
                                            {cfg.columns.map((c) => (
                                                <td key={c.label} className={`p-2 ${c.align === "right" ? "text-right" : "text-left"} ${c.wide ? "max-w-md" : ""}`}>
                                                    {c.wide ? <span className="whitespace-pre-wrap text-slate-700">{c.get(r)}</span> : c.get(r)}
                                                </td>
                                            ))}
                                            <td className="p-2 text-slate-500">{r.logged_by || r.set_by || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Mobile card list */}
                            <div className="sm:hidden divide-y divide-slate-100" data-testid="detail-cards">
                                {rows.map((r) => (
                                    <div key={r.id} className="p-3" data-testid={`detail-card-${r.id}`}>
                                        <div className="space-y-1">
                                            {cfg.columns.map((c) => (
                                                <div key={c.label} className="flex gap-2 text-[12px]">
                                                    <div className="text-slate-500 uppercase tracking-wider text-[9.5px] font-display font-bold w-24 flex-shrink-0 pt-0.5">{c.label}</div>
                                                    <div className={`flex-1 ${c.wide ? "" : "tabular"}`}>{c.get(r)}</div>
                                                </div>
                                            ))}
                                            {(r.logged_by || r.set_by) && (
                                                <div className="flex gap-2 text-[11px] text-slate-500 pt-1 border-t border-slate-100 mt-1">
                                                    <div className="uppercase tracking-wider text-[9.5px] font-display font-bold w-24 flex-shrink-0">Logged by</div>
                                                    <div className="flex-1">{r.logged_by || r.set_by}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <div className="px-3 sm:px-4 py-2 border-t border-slate-200 text-[10.5px] text-slate-500 italic">
                    Read-only view. To edit or delete entries, use the Manage button in the capture panel.
                </div>
            </div>
        </div>
    );
}

function ExecSummaryDetail({ data, onClose }) {
    const e = data.executive_summary || {};
    const cmp = data.period_comparison || {};
    const fmtN = (n) => Number(n || 0).toLocaleString();
    const rows = [
        { label: "Vessels operated",       now: e.vessels_operated,                prev: cmp.previous?.vessels_operated },
        { label: "Voyages completed",      now: e.voyages_completed,               prev: undefined },
        { label: "Total cargo (t)",        now: fmtN(e.total_cargo_tons),          prev: fmtN(cmp.previous?.total_cargo_tons) },
        { label: "Target cargo (t)",       now: fmtN(e.target_cargo_tons),         prev: undefined },
        { label: "On-time %",              now: `${(e.on_time_pct || 0).toFixed(1)}%`, prev: `${(cmp.previous?.on_time_pct || 0).toFixed(1)}%` },
        { label: "Avg turnaround (hrs)",   now: (e.avg_turnaround_hrs || 0).toFixed(1), prev: (cmp.previous?.avg_turnaround_hrs || 0).toFixed(1) },
        { label: "Berth utilisation %",    now: `${(e.utilisation_pct || 0).toFixed(1)}%`, prev: `${(cmp.previous?.utilisation_pct || 0).toFixed(1)}%` },
        { label: "Safety incidents",       now: e.safety_incidents,                prev: undefined },
        { label: "LTIFR incidents",        now: e.ltifr_count,                     prev: undefined },
        { label: "Client complaints",      now: e.client_complaints,               prev: undefined },
    ];
    return (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
            <div className="g-card w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(ev) => ev.stopPropagation()} data-testid="detail-modal-executive_summary">
                <div className="g-card-header-dark">
                    <Info size={14} weight="fill" />
                    <div className="font-display font-black uppercase tracking-wide">Executive Summary — Detail</div>
                    <button onClick={onClose} className="ml-auto text-white text-lg leading-none">×</button>
                </div>
                <div className="p-4 overflow-auto text-sm">
                    <p className="text-slate-600 mb-3">
                        Every figure on the Executive Summary card and how it compares to the previous period
                        ({cmp.previous?.period_label || "—"}). Use this to brief stakeholders without leaving the dashboard.
                    </p>
                    <table className="w-full text-xs tabular" data-testid="exec-detail-table">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                            <tr>
                                <th className="text-left p-2">Metric</th>
                                <th className="text-right p-2">Current</th>
                                <th className="text-right p-2">Previous</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.label} className="border-t border-slate-100">
                                    <td className="p-2 text-slate-700">{r.label}</td>
                                    <td className="p-2 text-right font-semibold text-[var(--grindrod-navy)]">{r.now ?? "—"}</td>
                                    <td className="p-2 text-right text-slate-500">{r.prev ?? "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function TrendsDetail({ data, onClose }) {
    const cmp = data.period_comparison || {};
    const e = data.executive_summary || {};
    const fmtPctv = (n) => `${(Number(n) || 0).toFixed(1)}%`;
    const rows = [
        { label: "On-time %", now: fmtPctv(e.on_time_pct), prev: fmtPctv(cmp.previous?.on_time_pct), delta: cmp.deltas?.on_time_pct, unit: "%", better: "higher" },
        { label: "Avg turnaround (hrs)", now: (e.avg_turnaround_hrs || 0).toFixed(1), prev: (cmp.previous?.avg_turnaround_hrs || 0).toFixed(1), delta: cmp.deltas?.avg_turnaround_hrs, unit: "h", better: "lower" },
        { label: "Berth utilisation %", now: fmtPctv(e.utilisation_pct), prev: fmtPctv(cmp.previous?.utilisation_pct), delta: cmp.deltas?.utilisation_pct, unit: "%", better: "higher" },
        { label: "Total cargo (t)",  now: Number(e.total_cargo_tons || 0).toLocaleString(), prev: Number(cmp.previous?.total_cargo_tons || 0).toLocaleString(), delta: cmp.deltas?.total_cargo_tons, unit: "t", better: "higher" },
    ];
    return (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
            <div className="g-card w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(ev) => ev.stopPropagation()} data-testid="detail-modal-trends">
                <div className="g-card-header-dark">
                    <Info size={14} weight="fill" />
                    <div className="font-display font-black uppercase tracking-wide">Performance Trends — How we calculate this</div>
                    <button onClick={onClose} className="ml-auto text-white text-lg leading-none">×</button>
                </div>
                <div className="p-4 overflow-auto text-sm space-y-3">
                    <div className="rounded-sm bg-slate-50 border border-slate-200 p-3 text-[12px] text-slate-700 space-y-1.5">
                        <div><b>On-Time Performance</b> = vessels that left on or before the planned ETB ÷ all vessels in the period, expressed as a percentage. Higher is better.</div>
                        <div><b>Avg Turnaround</b> = average elapsed hours from vessel start to completion. Lower is better.</div>
                        <div><b>Berth Utilisation</b> = average proportion of berth-time the wharf was actually loading. Higher is better.</div>
                        <div className="pt-1 text-slate-500 italic">Previous-period figures use the prior month (or prior 14-day window) on identical rules — so the trend arrow tells you whether the wharf is improving or slipping.</div>
                    </div>
                    <table className="w-full text-xs tabular" data-testid="trends-detail-table">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                            <tr>
                                <th className="text-left p-2">Metric</th>
                                <th className="text-right p-2">Current</th>
                                <th className="text-right p-2">Previous ({cmp.previous?.period_label || "—"})</th>
                                <th className="text-right p-2">Change</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const isUp = (r.better === "higher" && (r.delta?.diff || 0) >= 0)
                                          || (r.better === "lower" && (r.delta?.diff || 0) <= 0);
                                const colour = (r.delta?.diff || 0) === 0 ? "#64748B" : isUp ? "var(--grindrod-green)" : "var(--grindrod-red)";
                                return (
                                    <tr key={r.label} className="border-t border-slate-100">
                                        <td className="p-2 text-slate-700">{r.label}</td>
                                        <td className="p-2 text-right font-semibold">{r.now}</td>
                                        <td className="p-2 text-right text-slate-500">{r.prev}</td>
                                        <td className="p-2 text-right font-semibold tabular" style={{ color: colour }}>
                                            {r.delta?.diff !== undefined
                                                ? `${r.delta.diff > 0 ? "+" : ""}${r.delta.diff}${r.unit}${r.delta.pct !== null ? ` (${r.delta.pct > 0 ? "+" : ""}${r.delta.pct}%)` : ""}`
                                                : "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function VesselPerformanceDetail({ data, onClose }) {
    const rows = data.vessel_performance || [];
    return (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
            <div className="g-card w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={(ev) => ev.stopPropagation()} data-testid="detail-modal-vessel_performance">
                <div className="g-card-header-dark">
                    <Info size={14} weight="fill" />
                    <div className="font-display font-black uppercase tracking-wide">Vessel Performance — Per vessel</div>
                    <button onClick={onClose} className="ml-auto text-white text-lg leading-none">×</button>
                </div>
                <div className="p-4 overflow-auto text-sm">
                    {rows.length === 0 ? (
                        <div className="text-center text-slate-400 py-8">No vessels in this period.</div>
                    ) : (
                        <table className="w-full text-xs tabular" data-testid="vessel-perf-detail-table">
                            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                                <tr>
                                    <th className="text-left p-2">Vessel</th>
                                    <th className="text-left p-2">Client</th>
                                    <th className="text-right p-2">Cargo (t)</th>
                                    <th className="text-right p-2">On-time</th>
                                    <th className="text-right p-2">Turnaround (h)</th>
                                    <th className="text-right p-2">Utilisation</th>
                                    <th className="text-center p-2">Rating</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={r.vessel_name || i} className="border-t border-slate-100">
                                        <td className="p-2 font-semibold text-[var(--grindrod-navy)]">{r.vessel_name}</td>
                                        <td className="p-2 text-slate-600">{r.client_name || "—"}</td>
                                        <td className="p-2 text-right">{Number(r.cargo_tons || 0).toLocaleString()}</td>
                                        <td className={`p-2 text-right font-semibold ${r.on_time_pct === 100 ? "text-[var(--grindrod-green)]" : "text-[var(--grindrod-red)]"}`}>{(r.on_time_pct || 0).toFixed(1)}%</td>
                                        <td className="p-2 text-right">{(r.avg_turnaround_hrs || 0).toFixed(1)}</td>
                                        <td className="p-2 text-right">{(r.utilisation_pct || 0).toFixed(1)}%</td>
                                        <td className="p-2 text-center capitalize">
                                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${({good:"bg-[var(--grindrod-green)]",avg:"bg-[#FFB300]",poor:"bg-[var(--grindrod-red)]"})[r.rating] || "bg-slate-300"}`}></span>
                                            <span className="ml-2 text-[11px] text-slate-600">{r.rating}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
